import { SparksIcon } from '@/common/svg/spark';
import './index.scss';
import {
  Button,
  Modal,
  Select,
  Drawer,
  List,
  Typography,
  Spin,
  Input,
  Popconfirm,
  message,
  Radio,
  Space,
  Table,
  Alert,
} from 'antd';
import {
  CloseOutlined,
  CheckOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  FileAddOutlined,
  EditOutlined,
  FileTextOutlined,
  BankOutlined,
  BulbOutlined,
  BranchesOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  DollarOutlined,
  LinkOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowUpOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { StopCircleFilled } from '@/common/svg/stop-circle-filled';
import { LinkV2Icon } from '@/common/svg/link-v2';
import { GlobeV2Icon } from '@/common/svg/globe-v2';
import { AnalyzingBulbIcon } from '@/common/svg/analyzing-bulb';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useFirmDataEnabled from '@/hooks/useFirmDataEnabled';
import { useWsConnectionGuard } from '@/hooks/useWsConnectionGuard';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  addUserMessage,
  setIsLoading,
  setIsWaitingForResponse,
  addAIMessage,
  updateLastMessage,
  resetProgressState,
  advanceProgressPhase,
  resetProgressPhase,
  updateConversationContextFiles,
  removeConversationContextFile,
  updateConversationAdditionalContextJson,
  updateVisualization,
  addSystemMessage,
  addToolMessage,
  upsertToolStatusMessage,
  finalizeToolStatusMessage,
  setConversation,
  updateConversationContextFileStatus,
  updatePdfProcessingProgress,
  resetPdfProcessingProgress,
  setWebSearchEnabled,
  selectWebSearchEnabled,
  triggerCheckpointRefresh,
  setActiveRunId,
  setIsCancellingRun,
  setPendingInterrupt,
  clearPendingInterrupt,
  setResumeInFlight,
  setResumeError,
  selectPendingInterrupt,
  selectHasPendingInterrupt,
  selectIsResumeInFlight,
  selectResumeError,
} from '@/store/features/canvas/canvasSlice';
import {
  extractThreadNameFromState,
  extractVisualizationHtmlFromState,
  mapAgentMessagesToConversation,
} from '@/common/components/wavvest-planning/orchestrator-message-adapter';
import { parsePlanningWsEvent } from '@/common/components/wavvest-planning/planning-ws-event-parser';
import type { PlanningWsRawEvent } from '@/common/components/wavvest-planning/planning-ws-event-parser';
import {
  deriveComposerRunControls,
  STUCK_RUN_RECOVERY_DELAY_MS,
} from '@/common/components/wavvest-planning/conversation-panel/composer-run-controls';
import {
  PHASE_LABELS,
  phaseForNodeNames,
} from '@/common/components/wavvest-planning/streaming-progress';
import canvasWebSocket from '@/services/websocket-service';
import { store } from '@/store';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import {
  normalizeLiteralEscapes,
  sanitizeMarkdownUrl,
  shouldUseGfm,
} from '@/common/components/wavvest-planning/conversation-panel/markdown-security';
import {
  AdditionalContextJson,
  ContextFile,
  InterruptKind,
  InterruptResume,
  Thread,
  WebSocketResponse,
} from '@/common/components/wavvest-planning/types';
import {
  DEFAULT_PLANNER_MODE,
  PLANNER_MODE_DESCRIPTIONS,
  PLANNER_MODE_LABELS,
  extractPlannerModeFromThreadState,
  plannerModeToCreativeMode,
} from '@/common/components/wavvest-planning/planner-mode';
import type { PlannerMode } from '@/common/components/wavvest-planning/planner-mode';
import {
  extractPlannerEngineFromThreadState,
  PLANNER_ENGINE_LABELS,
  type PlannerEngine,
} from '@/common/components/wavvest-planning/planner-engine';
import { mapParsedInterruptToGraphInterrupt } from '@/common/components/wavvest-planning/interrupt-adapter';

import {
  ALLOWED_FILE_EXTENSIONS,
  TOOLCALL_NAME_DISPLAY_MAP,
} from '@/common/components/wavvest-planning/constants';
import {
  extractLeafContextFileKey,
  buildFullContextFileKey,
} from '@/common/components/wavvest-planning/file-key-utils';
import { processFilesForImmediateUpload } from '../utils';
import { MessageRequestBody } from '../types';
import { message as antMessage, Popover, Tooltip } from 'antd';
import { usePlaidData } from '@/store/hooks';
import {
  deleteContextFile,
  reattachContextFiles,
  type ReattachContextFilesResponse,
} from '@/api/weave-x';
import DragOverlay from '../file-drag';
import PdfProcessingProgress from '../pdf-processing-progress';
import {
  getAllThreads,
  deleteThread,
  getResumableCheckpoints,
  fetchChatHistoryOnCheckpoint,
  fetchThreadStateOnCheckpoint,
  cancelThreadRun,
  patchThreadMetadata,
  getPlannerInvestorOptions,
  type PlannerInvestorOption,
  type AgentId,
} from '@/api/weave-x-agents';
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import { getAttendedMeetings, getDocument, AttendedMeeting } from '@/api/custom';
import { getInvestorDisplayName, getInvestorNameOrFallback } from '@/util/investor-display';
import {
  findInvestorByCognitoId,
  getInvestorThreadGroup,
  groupThreads,
  sortInvestorsByDisplayName,
  type ThreadGroup,
} from '@/common/components/wavvest-planning/conversation-panel/session-history-utils';
import {
  hydrateThreadsWithHistoryTitles,
  restorePlannerSession,
} from '@/common/components/wavvest-planning/conversation-panel/session-loader';
import {
  parsePlannerInvestorProfileSelection,
  plannerInvestorProfileSelectionFromSources,
  plannerProfileSessionOptions,
  resolvePlannerProfileSelectionForSession,
  shouldStartNewPlannerProfileSession,
  userClientIdFromStateUser,
  type PlannerInvestorProfileSelection,
} from '@/common/components/wavvest-planning/conversation-panel/planner-profile-session';
import { replaceSuggestedPromptsWithNames } from '@/common/components/wavvest-planning/conversation-panel/history-message-display';
import {
  selectApexEnabled,
  selectApexCorrespondentId,
  selectUser,
} from '@/store/features/userinfo/userinfoSlice';
import {
  getApexAccountData,
  getApexAccountOverview,
  createSessionAssociation,
} from '@/api/weave-x';

type AgentCheckpoint =
  Awaited<ReturnType<typeof getResumableCheckpoints>> extends Array<infer U> ? U : never;

const markdownComponents: Components = {
  table: ({ children }) => (
    <div className="markdown-table-wrapper" tabIndex={0} aria-label="Scrollable markdown table">
      <table className="markdown-table">{children}</table>
    </div>
  ),
  // Security: do not render markdown images from model output.
  img: () => null,
};

const markdownRemarkPlugins = [remarkGfm] as const;
const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
  attributes: {
    ...defaultSchema.attributes,
    div: [
      ...(((defaultSchema.attributes?.div as unknown[]) ?? []) as unknown[]),
      ['className', 'markdown-table-wrapper'],
      'tabIndex',
      'ariaLabel',
    ],
    table: [
      ...(((defaultSchema.attributes?.table as unknown[]) ?? []) as unknown[]),
      ['className', 'markdown-table'],
    ],
    section: [
      ...(((defaultSchema.attributes?.section as unknown[]) ?? []) as unknown[]),
      'dataFootnotes',
      'className',
    ],
    a: [
      ...(((defaultSchema.attributes?.a as unknown[]) ?? []) as unknown[]),
      'dataFootnoteRef',
      'dataFootnoteBackref',
      'ariaDescribedBy',
      'ariaLabel',
      'className',
    ],
    sup: [...(((defaultSchema.attributes?.sup as unknown[]) ?? []) as unknown[]), 'id'],
    li: [...(((defaultSchema.attributes?.li as unknown[]) ?? []) as unknown[]), 'id'],
    h2: [...(((defaultSchema.attributes?.h2 as unknown[]) ?? []) as unknown[]), 'id', 'className'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto', 'tel'],
    src: ['http', 'https'],
  },
};
const markdownRehypePlugins = [[rehypeSanitize, markdownSanitizeSchema]] as const;

function renderMessageContent(
  content: string,
  sender: 'human' | 'ai' | 'system' | 'tool',
): React.ReactNode {
  // Defensive normalization: agent output sometimes contains the literal
  // two-character sequence "\n" instead of a real newline, which markdown
  // would otherwise render as text. Only touch model-generated content.
  const normalizedContent =
    sender === 'ai' || sender === 'tool' ? normalizeLiteralEscapes(content) : content;

  const remarkPlugins = shouldUseGfm(sender, normalizedContent) ? markdownRemarkPlugins : undefined;

  return (
    <Markdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={markdownRehypePlugins}
      disallowedElements={['img']}
      urlTransform={sanitizeMarkdownUrl}
      components={markdownComponents}
    >
      {normalizedContent}
    </Markdown>
  );
}

function shouldAppendConversationMessage(message: {
  sender: 'human' | 'ai' | 'system' | 'tool';
  content: string;
}): boolean {
  const messages = store.getState().canvas.conversation.messages;
  const lastMessage = messages[messages.length - 1];
  return lastMessage?.sender !== message.sender || lastMessage?.content !== message.content;
}

function allRequestedContextFilesReattached(
  fileKeys: string[],
  response: ReattachContextFilesResponse,
): boolean {
  const requestedFileKeys = new Set(fileKeys);
  if (requestedFileKeys.size !== fileKeys.length) return false;

  const successfulFileKeys = new Set<string>();
  for (const file of response.reattachedFileKeys) {
    const fileKey = extractLeafContextFileKey(file.fileKey);
    if (
      file.status !== 'success' ||
      !requestedFileKeys.has(fileKey) ||
      successfulFileKeys.has(fileKey)
    ) {
      return false;
    }
    successfulFileKeys.add(fileKey);
  }

  return successfulFileKeys.size === requestedFileKeys.size;
}

async function reattachContextFilesToSession(
  contextFiles: ContextFile[],
  options: {
    sourceSessionId: string;
    targetSessionId: string;
    userId: string;
  },
): Promise<boolean> {
  const { sourceSessionId, targetSessionId, userId } = options;
  const fileKeys = contextFiles
    .filter((file) => file.uploaded && file.fileKey)
    .map((file) => extractLeafContextFileKey(file.fileKey!));

  if (fileKeys.length === 0) return true;

  const response = await reattachContextFiles({
    userId,
    sourceSessionId,
    targetSessionId,
    fileKeys,
  });
  return allRequestedContextFilesReattached(fileKeys, response);
}

function additionalContextForPlannerRequest(
  additionalContext: AdditionalContextJson | undefined,
): AdditionalContextJson | undefined {
  if (!additionalContext) return undefined;
  const requestContext = { ...additionalContext };
  delete requestContext.plannerInvestorProfile;
  delete requestContext.plannerInvestorProfileSubmitted;
  return Object.keys(requestContext).length > 0 ? requestContext : undefined;
}

function additionalContextWithoutPlannerProfile(
  additionalContext: AdditionalContextJson | undefined,
): AdditionalContextJson {
  const context = { ...(additionalContext || {}) };
  delete context.plannerInvestorProfile;
  delete context.plannerInvestorProfileSubmitted;
  return context;
}

function retainedPlannerProfileContext(
  additionalContext: AdditionalContextJson | undefined,
  options: { submitted?: boolean } = {},
): AdditionalContextJson {
  const selection = parsePlannerInvestorProfileSelection(additionalContext?.plannerInvestorProfile);
  return selection
    ? {
        plannerInvestorProfile: JSON.stringify(selection),
        ...(options.submitted ? { plannerInvestorProfileSubmitted: true } : {}),
      }
    : {};
}

function plannerInvestorProfilePrompt(selection: PlannerInvestorProfileSelection): string {
  return `Use ${selection.displayName}'s selected investor profile for this planning request.`;
}

interface ConversationPanelProps {
  inputVariant?: ChatInputVariant;
  hideHeader?: boolean;
  className?: string;
  footerAddon?: React.ReactNode;
  v2ToolbarStartContent?: React.ReactNode;
  v2ComposerNotice?: React.ReactNode;
  plannerMode?: PlannerMode;
  plannerAgentId?: AgentId;
  /** When set (timestamp in ms), messages scrollbar stays hidden until after this time. Used after divider resize. */
  scrollbarSuppressedUntil?: number;
}

function ConversationPanel({
  inputVariant = 'default',
  hideHeader = false,
  className = '',
  footerAddon,
  v2ToolbarStartContent,
  v2ComposerNotice,
  plannerMode = DEFAULT_PLANNER_MODE,
  plannerAgentId,
  scrollbarSuppressedUntil,
}: ConversationPanelProps = {}) {
  const dispatch = useAppDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const { isWaitingForResponse } = useAppSelector((state) => state.canvas);
  const hasPendingInterrupt = useAppSelector(selectHasPendingInterrupt);
  const useV2LoadingIndicator = inputVariant === 'v2';

  const handleFilesAdded = async (files: File[]) => {
    // While an interruption is pending the composer is blocked; the drag-and-drop
    // upload path routes through here, so refuse uploads until the user resolves it.
    if (hasPendingInterrupt) {
      return;
    }
    const { user } = store.getState().userinfo;
    const currentFiles = store.getState().canvas.conversation.contextFiles || [];
    if (!user?.id) {
      message.error('Unable to upload files. Please sign in again.');
      return;
    }

    const { contextFiles, errors } = await processFilesForImmediateUpload(
      files,
      user.id,
      (fileName, updates) => {
        dispatch(updateConversationContextFileStatus({ fileName, updates }));
      },
      currentFiles,
    );

    errors.forEach((error) => {
      antMessage.error(error);
    });

    if (contextFiles.length > 0) {
      dispatch(updateConversationContextFiles([...currentFiles, ...contextFiles]));
      antMessage.success(`${contextFiles.length} file(s) added and uploading...`);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Keep the counter paired with handleDragLeave regardless of interrupt state —
    // early-returning before the increment would desync it against the decrement
    // and break the overlay on later drags. Only suppress the visual overlay while
    // an interruption is pending; the actual upload is blocked in handleFilesAdded.
    dragCounter.current++;
    if (dragCounter.current === 1 && !hasPendingInterrupt) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDropCapture = () => {
    dragCounter.current = 0;
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const files = Array.from(e.dataTransfer.files);
    await handleFilesAdded(files);
  };

  useEffect(() => {
    const dispatchStoppedRunState = () => {
      dispatch(setActiveRunId(null));
      dispatch(setIsCancellingRun(false));
      dispatch(setIsLoading(false));
      dispatch(setIsWaitingForResponse(false));
      dispatch(resetProgressPhase());
      dispatch(finalizeToolStatusMessage());
    };

    const dispatchTerminalState = () => {
      dispatch(triggerCheckpointRefresh(Date.now()));
      dispatchStoppedRunState();
    };

    const listener = {
      onMessage: (response: WebSocketResponse) => {
        // All raw weave-x payload parsing is centralized in parsePlanningWsEvent.
        // The component reacts only to normalized Planning events, so unknown or
        // v4 event shapes can never crash the UI.
        const parsedEvents = parsePlanningWsEvent(response as unknown as PlanningWsRawEvent);

        for (const event of parsedEvents) {
          // Resume resolution (ENG-2713): if a resume is in flight and the run has
          // produced meaningful progress, the interrupt is resolved — clear
          // it so the UI returns to normal and input re-enables. Read isResumeInFlight
          // from the store snapshot, not a stale closure. A fresh 'interrupt' replaces
          // the card (outcome 2); an 'error' is a resume failure kept open by the
          // error case (outcome 3) — so neither clears here, and we never clear
          // optimistically on send (only once the run truly resumes).
          //
          // Metadata, node-status, PDF progress, and unknown frames only prove that
          // transport is alive; they do not prove the resume was accepted. Keep the
          // card (and its local draft) mounted until a conversation/output/tool event
          // or terminal completion confirms that the resumed graph actually advanced.
          if (
            store.getState().canvas.isResumeInFlight &&
            (event.kind === 'message' ||
              event.kind === 'visualization' ||
              event.kind === 'tool_status' ||
              event.kind === 'complete')
          ) {
            dispatch(clearPendingInterrupt());
          }

          switch (event.kind) {
            case 'run_metadata': {
              if (event.runId) {
                dispatch(setActiveRunId(event.runId));
                dispatch(setIsCancellingRun(false));
              }
              break;
            }

            case 'message': {
              if (event.source === 'stream') {
                if (!useV2LoadingIndicator && isWaitingForResponse) {
                  dispatch(setIsWaitingForResponse(false));
                }
                const messages = store.getState().canvas.conversation.messages;
                if (messages.length > 0) {
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage && lastMessage.sender === 'ai') {
                    dispatch(updateLastMessage(event.content));
                  } else {
                    dispatch(addAIMessage(event.content));
                  }
                } else {
                  console.warn('No messages in conversation');
                }
              } else {
                if (!shouldAppendConversationMessage(event)) {
                  break;
                }
                if (event.sender === 'ai') {
                  dispatch(addAIMessage(event.content));
                } else if (event.sender === 'human') {
                  dispatch(addUserMessage(event.content));
                } else if (event.sender === 'tool') {
                  dispatch(addToolMessage(event.content));
                } else {
                  dispatch(addSystemMessage(event.content));
                }
              }
              break;
            }

            case 'visualization': {
              dispatch(updateVisualization({ sourceCode: event.html }));
              dispatch(resetProgressState());
              dispatch(setIsWaitingForResponse(false));
              dispatch(setIsLoading(false));
              dispatch(finalizeToolStatusMessage());
              break;
            }

            case 'node_status': {
              const candidatePhase = phaseForNodeNames(event.nodeNames);
              console.log('[planning-v2/progress] received node status', {
                nodeNames: event.nodeNames,
                candidatePhase,
                currentPhase: store.getState().canvas.progressPhase,
              });
              if (candidatePhase !== null) {
                dispatch(advanceProgressPhase(candidatePhase));
              }
              break;
            }

            case 'tool_status': {
              if (event.status === 'start') {
                if (!useV2LoadingIndicator && isWaitingForResponse) {
                  dispatch(setIsWaitingForResponse(false));
                }
                const toolName = event.tool || 'Working on your requests...';
                const displayName =
                  TOOLCALL_NAME_DISPLAY_MAP[toolName as keyof typeof TOOLCALL_NAME_DISPLAY_MAP] ??
                  toolName;
                dispatch(upsertToolStatusMessage(displayName));
                if (displayName === TOOLCALL_NAME_DISPLAY_MAP.visualization_toolcall) {
                  dispatch(setIsLoading(true));
                }
              } else if (event.status === 'end' && event.tool === 'generate_visualization') {
                // The custom tool_end for the visualization tool posts an empty AI turn.
                dispatch(addAIMessage(''));
              }
              break;
            }

            case 'pdf_progress': {
              dispatch(updatePdfProcessingProgress(event.payload));
              if (event.done) {
                setTimeout(() => {
                  dispatch(resetPdfProcessingProgress());
                }, 3000);
              }
              break;
            }

            case 'interrupt': {
              // An interrupt is neither the AI actively working nor the run
              // finishing, so the normal waiting/loading spinners must come down
              // while we ask the user, and any leftover progress bar is cleared.
              dispatch(setIsWaitingForResponse(false));
              dispatch(setIsLoading(false));
              // Clear both progress systems: the legacy percent bar (progressState)
              // and the v4 streaming phase (progressPhase) so nothing lingers behind
              // the pending card.
              dispatch(resetProgressState());
              dispatch(resetProgressPhase());
              dispatch(finalizeToolStatusMessage());

              // The parser hands us a PlanningInterrupt ({ type, payload }). A single
              // adapter maps it to our internal GraphInterrupt — the only place that
              // touches the parser's shape. Returns null when it can't be resumed
              // (no token). Do NOT push it into the message list — the pending
              // interrupt renders its own UI (ENG-2713).
              const interrupt = mapParsedInterruptToGraphInterrupt(event.interrupt);
              if (!interrupt) {
                console.error('Received interrupt without a valid payload', event.interrupt);
                dispatch(
                  addSystemMessage(
                    'Something went wrong while pausing for your input. Please try again.',
                  ),
                );
                break;
              }
              dispatch(setPendingInterrupt(interrupt));
              break;
            }

            case 'complete': {
              dispatchTerminalState();
              break;
            }

            case 'error': {
              dispatchStoppedRunState();

              // Resume failure (ENG-2713 outcome 3): if a resume was in flight, keep
              // the interrupt open and the answer intact (the card stays mounted),
              // show the error ON the card, and leave submit available for retry.
              // setResumeError also drops the in-flight flag. No scary template msg.
              if (store.getState().canvas.isResumeInFlight) {
                dispatch(
                  setResumeError('We were unable to continue the conversation. Please try again.'),
                );
                break;
              }

              // Note: graph interruptions no longer arrive here. They are delivered
              // as a normalized 'interrupt' event handled by the 'interrupt' case
              // above (ENG-2713), which replaced the previous UserInterrupt placeholder.
              dispatch(
                addSystemMessage(
                  'We are unable to process your request at the moment. You may need to save your work as an organization template and start a new conversation based on that template.',
                ),
              );
              break;
            }

            case 'unknown': {
              // Unknown event shape: safely ignored so the UI never crashes.
              break;
            }
          }
        }
      },

      onError: (error: Event) => {
        antMessage.error('Error: ' + error.toString());
        dispatchStoppedRunState();

        // A socket drop after a good resume send is a resume failure (outcome 3):
        // keep the interrupt open, show the error on the card, leave submit for
        // retry. setResumeError also drops the in-flight flag so the card is not
        // stranded on "Sending…".
        if (store.getState().canvas.isResumeInFlight) {
          dispatch(setResumeError('Connection lost. Please try again.'));
          return;
        }
        dispatch(
          addSystemMessage(
            'An error occurred while processing your request. Please try again later.',
          ),
        );
      },
      onDisconnect: () => {
        dispatchStoppedRunState();
      },
    };

    canvasWebSocket.addListener(listener);

    return () => {
      canvasWebSocket.removeListener(listener);
    };
  }, [dispatch, isWaitingForResponse, useV2LoadingIndicator]);

  // Fire the resume the interrupt card assembled: mark it in-flight, send the
  // ResumeRequest over the websocket, then let the normal event stream take over
  // (stream/updates/interrupt/error) — step 7 clears the interrupt once the
  // resumed run advances. The checkpoint, if the interrupt carried one, rides
  // along so the backend resumes from the right point.
  const handleInterruptResume = useCallback(
    (resume: InterruptResume) => {
      const checkpoint = store.getState().canvas.pendingInterrupt?.checkpoint;
      dispatch(setResumeInFlight(true));

      const sent = canvasWebSocket.sendResumeRequest(resume, checkpoint);
      if (!sent) {
        // Synchronous failure (socket not open): treat as a resume failure —
        // setResumeError drops the in-flight flag and leaves pendingInterrupt set,
        // so the card stays mounted and the user's draft survives for retry.
        dispatch(
          setResumeError('Unable to send your answer. Please check your connection and try again.'),
        );
      } else {
        dispatch(setIsWaitingForResponse(true));
        dispatch(setIsLoading(false));
      }
    },
    [dispatch],
  );

  return (
    <div
      className={`conversation-panel ${className}`.trim()}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDropCapture={handleDropCapture}
      onDrop={handleDrop}
    >
      <DragOverlay isDragging={isDragging} />
      {!hideHeader && <ConversationPanelHeader />}
      <ConversationPanelBody
        useV2LoadingIndicator={useV2LoadingIndicator}
        scrollbarSuppressedUntil={scrollbarSuppressedUntil}
        onInterruptSubmit={handleInterruptResume}
      />
      <ConversationPanelFooter
        variant={inputVariant}
        plannerMode={plannerMode}
        plannerAgentId={plannerAgentId}
        v2ToolbarStartContent={v2ToolbarStartContent}
        v2ComposerNotice={v2ComposerNotice}
        footerAddon={footerAddon}
      />
    </div>
  );
}

export function ConversationPanelHeader({ isShowingMoreText }: { isShowingMoreText?: boolean }) {
  const location = useLocation();
  const preSelectedInvestor = useAppSelector(
    (state) => state.planning.guidedMode.investorSelector.selectedInvestor,
  );

  const isSandboxMode = useMemo(
    () => location.pathname.includes('/planning/sandbox-mode'),
    [location.pathname],
  );

  const shouldShowAutoAssignHint = isSandboxMode && preSelectedInvestor;

  return (
    <div className="conversation-panel-header">
      <div className="conversation-panel-header-top">
        <div className="conversation-panel-header-left">
          <SparksIcon />
          <h2>Weave X</h2>
        </div>
        {shouldShowAutoAssignHint && (
          <div className="conversation-panel-header-right">
            <span className="auto-assign-hint">
              Auto-assigning sessions to {getInvestorNameOrFallback(preSelectedInvestor.name)}
            </span>
          </div>
        )}
      </div>
      {isShowingMoreText && <p>I'm your AI assistant, here to help…</p>}
    </div>
  );
}

const ANALYZING_STATUS_MESSAGES = [
  'Analyzing your scenario...',
  'Reading your documents...',
  'Building your plan...',
  'Generating insights...',
  'Almost there...',
];

/**
 * Renders the pending graph interruption inline in the thread (ENG-2713).
 *
 * This is deliberately NOT a sender-typed message bubble: it has its own render
 * path, so it is never styled as an ordinary message nor mutated by the
 * streaming/append logic that rewrites the message list.
 *
 * The draft answer and selected option are local component state (the hybrid
 * model from step 2). Because a failed resume leaves pendingInterrupt set, this
 * card stays mounted across the failure and the user's in-progress answer
 * survives for retry.
 *
 * The control rendered depends on interrupt.kind, because the resume payload is
 * per-kind (step 6): staged-files is a yes/no include gate, missing-data is a
 * fill-or-mock choice, source-conflict picks a claim or supplies another value,
 * and approval/generic keep the option-or-text control.
 */

// Read a string[] off interrupt.details, tolerating a missing/other-typed value.
function stringArrayFromDetails(
  details: Record<string, unknown> | undefined,
  key: string,
): string[] {
  const value = details?.[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function InterruptCard({ onSubmit }: { onSubmit: (resume: InterruptResume) => void }) {
  const interrupt = useAppSelector(selectPendingInterrupt);
  const isResumeInFlight = useAppSelector(selectIsResumeInFlight);
  const resumeError = useAppSelector(selectResumeError);
  const plannerInvestorProfileValue = useAppSelector(
    (state) => state.canvas.conversation.additionalContextJson?.plannerInvestorProfile,
  );
  const plannerProfileSelection = useMemo(
    () => plannerInvestorProfileSelectionFromSources(plannerInvestorProfileValue),
    [plannerInvestorProfileValue],
  );
  const currentUser = useAppSelector((state) => state.userinfo.user);
  const conversationFiles = useAppSelector((state) => state.canvas.conversation.contextFiles);
  const [draftAnswer, setDraftAnswer] = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [missingDataFiles, setMissingDataFiles] = useState<ContextFile[]>([]);
  const [missingDataFileError, setMissingDataFileError] = useState<string | null>(null);
  const [deletingMissingDataFile, setDeletingMissingDataFile] = useState<string | null>(null);
  const missingDataFileInputRef = useRef<HTMLInputElement>(null);
  const pendingMissingDataFileUpdates = useRef(new Map<string, Partial<ContextFile>>());
  const missingDataUploadGeneration = useRef(0);

  useEffect(() => {
    missingDataUploadGeneration.current += 1;
    setMissingDataFiles([]);
    setMissingDataFileError(null);
    setDeletingMissingDataFile(null);
    pendingMissingDataFileUpdates.current.clear();
  }, [interrupt?.id]);

  if (!interrupt) {
    return null;
  }

  const base = { interruptId: interrupt.id, token: interrupt.token };
  const options = interrupt.options ?? [];
  const draft = draftAnswer.trim();

  // Per-kind header: a bold title next to a leading icon. Two of the kinds
  // (staged-files, missing-data) also lift their primary actions up into this
  // header row via the shell's headerAction slot. approval/generic fall back to
  // a neutral heading.
  const headingFor = (kind: InterruptKind): { title: string; icon: React.ReactNode } => {
    switch (kind) {
      case 'missing-data':
        return { title: 'More client data needed', icon: <DatabaseOutlined /> };
      case 'staged-files':
        return { title: 'Include staged documents?', icon: <FileTextOutlined /> };
      case 'source-conflict':
        return { title: 'Conflicting source data', icon: <BranchesOutlined /> };
      default:
        return { title: 'Weave X needs your input', icon: <BulbOutlined /> };
    }
  };

  // Shared card shell: a header row (icon + bold title + optional right-aligned
  // action slot), the question line, the kind-specific controls, then the inline
  // error line so a failed resume can be retried in place. Each kind may override
  // the title/icon or pass a header-action node; otherwise the heading is derived
  // from interrupt.kind.
  const shell = (
    controls: React.ReactNode,
    opts?: { title?: string; icon?: React.ReactNode; headerAction?: React.ReactNode },
  ) => {
    const heading = headingFor(interrupt.kind);
    const title = opts?.title ?? heading.title;
    const icon = opts?.icon ?? heading.icon;
    const headerAction = opts?.headerAction;
    return (
      <div
        className="interrupt-card"
        role="group"
        aria-label="The assistant needs your input"
        data-interrupt-kind={interrupt.kind}
      >
        <div className="interrupt-card-header">
          <div className="interrupt-card-heading">
            <span className="interrupt-card-icon" aria-hidden="true">
              {icon}
            </span>
            <div className="interrupt-card-label">{title}</div>
          </div>
          {headerAction && <div className="interrupt-card-header-actions">{headerAction}</div>}
        </div>
        <div className="interrupt-card-question">{interrupt.question}</div>
        {controls}
        {resumeError && (
          <div className="interrupt-card-error" role="alert">
            {resumeError}
          </div>
        )}
      </div>
    );
  };

  // A picked option and a typed value are mutually exclusive so a resume is never
  // silently ambiguous (matters for source-conflict, where both a claim and an
  // "other" value are possible): selecting a pill clears the text, and typing
  // clears the selection.
  const renderOptions = () => (
    <div className="interrupt-card-options">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`interrupt-card-option${selectedOptionId === option.id ? ' selected' : ''}`}
          aria-pressed={selectedOptionId === option.id}
          disabled={isResumeInFlight}
          onClick={() => {
            setSelectedOptionId(option.id);
            setDraftAnswer('');
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  // source-conflict shows the competing claims as full-width selectable rows:
  // the value (option.label) left-aligned, its provenance (option.sourceLabel,
  // Step 2) right-aligned and muted. Selection semantics match the pills above —
  // clicking a row selects it and clears any typed draft (mutual exclusivity).
  const renderConflictRows = () => (
    <div className="interrupt-card-rows">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`interrupt-card-row${selectedOptionId === option.id ? ' selected' : ''}`}
          aria-pressed={selectedOptionId === option.id}
          disabled={isResumeInFlight}
          onClick={() => {
            setSelectedOptionId(option.id);
            setDraftAnswer('');
          }}
        >
          <span className="interrupt-card-row-value">{option.label}</span>
          {option.sourceLabel && (
            <span className="interrupt-card-row-source">{option.sourceLabel}</span>
          )}
        </button>
      ))}
    </div>
  );

  const renderTextarea = (placeholder: string, onEnter?: () => void, disabled?: boolean) => (
    <textarea
      className="interrupt-card-input"
      value={draftAnswer}
      onChange={(event) => {
        const value = event.target.value;
        setDraftAnswer(value);
        if (value.length > 0) {
          setSelectedOptionId(null);
        }
      }}
      placeholder={placeholder}
      rows={2}
      disabled={disabled ?? isResumeInFlight}
      onKeyDown={(event) => {
        if (onEnter && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          onEnter();
        }
      }}
    />
  );

  const submitLabel = isResumeInFlight ? 'Sending…' : 'Submit';

  // staged-files: a binary include gate — each button is its own submit. Both
  // choices live in the header action slot (Step 3): Skip declines, Include
  // (affirmative, success-styled) accepts. The body is just the question line.
  if (interrupt.kind === 'staged-files') {
    const answer = (include: boolean) => onSubmit({ ...base, kind: 'staged-files', include });
    const stagedActions = (
      <>
        <button
          type="button"
          className="interrupt-card-secondary"
          disabled={isResumeInFlight}
          onClick={() => answer(false)}
        >
          <CloseOutlined aria-hidden /> Skip
        </button>
        <button
          type="button"
          className="interrupt-card-submit interrupt-card-submit--success"
          disabled={isResumeInFlight}
          onClick={() => answer(true)}
        >
          <CheckOutlined aria-hidden /> Include
        </button>
      </>
    );
    return shell(null, { headerAction: stagedActions });
  }

  // missing-data: show the outstanding fields as chips (plus what's already
  // captured), and let the user fill them in or ask the assistant to mock them.
  if (interrupt.kind === 'missing-data') {
    const profileFillText = plannerProfileSelection
      ? plannerInvestorProfilePrompt(plannerProfileSelection)
      : '';
    const hasSelectedFiles = missingDataFiles.length > 0;
    const isUploadingFiles = missingDataFiles.some((file) => file.uploading);
    const uploadedFiles = missingDataFiles.filter(
      (file): file is ContextFile & { fileKey: string } => Boolean(file.uploaded && file.fileKey),
    );
    const allSelectedFilesUploaded =
      hasSelectedFiles && uploadedFiles.length === missingDataFiles.length;
    const canFill =
      !isResumeInFlight &&
      !isUploadingFiles &&
      (draft.length > 0 || allSelectedFilesUploaded || (!hasSelectedFiles && profileFillText));
    const submitFill = () => {
      if (!canFill) return;
      onSubmit({
        ...base,
        kind: 'missing-data',
        choice: 'fill',
        ...(draft || (!hasSelectedFiles && profileFillText)
          ? { fillText: draft || profileFillText }
          : {}),
        ...(uploadedFiles.length > 0
          ? {
              files: uploadedFiles.map((file) => ({
                fileKey: file.fileKey,
                type: file.type,
              })),
            }
          : {}),
      });
    };
    const addMissingDataFiles = async (files: File[]) => {
      if (files.length === 0 || draft.length > 0 || isResumeInFlight) return;

      const uploadGeneration = missingDataUploadGeneration.current;
      setMissingDataFileError(null);
      if (!currentUser?.id) {
        setMissingDataFileError('Unable to identify the current user. Please sign in again.');
        return;
      }
      const { contextFiles, errors } = await processFilesForImmediateUpload(
        files,
        currentUser.id,
        (fileName, updates) => {
          if (missingDataUploadGeneration.current !== uploadGeneration) return;

          setMissingDataFiles((current) => {
            if (!current.some((file) => file.fileName === fileName)) {
              pendingMissingDataFileUpdates.current.set(fileName, updates);
              return current;
            }
            return current.map((file) =>
              file.fileName === fileName ? { ...file, ...updates } : file,
            );
          });
        },
        [...(conversationFiles ?? []), ...missingDataFiles],
      );

      if (missingDataUploadGeneration.current !== uploadGeneration) return;

      if (errors.length > 0) {
        setMissingDataFileError(errors.join(' '));
      }
      if (contextFiles.length > 0) {
        setMissingDataFiles((current) => [
          ...current,
          ...contextFiles.map((file) => ({
            ...file,
            ...pendingMissingDataFileUpdates.current.get(file.fileName),
          })),
        ]);
        contextFiles.forEach((file) => pendingMissingDataFileUpdates.current.delete(file.fileName));
      }
    };
    const removeMissingDataFile = async (file: ContextFile) => {
      if (file.uploading || deletingMissingDataFile) return;

      setMissingDataFileError(null);
      if (file.uploaded && file.fileKey) {
        const sessionId = canvasWebSocket.getSessionId();
        if (!sessionId || !currentUser?.id) {
          setMissingDataFileError('No active session found. Please try again.');
          return;
        }

        setDeletingMissingDataFile(file.fileName);
        try {
          const wasDeleted = await deleteContextFile(
            buildFullContextFileKey({
              fileKey: file.fileKey,
              userId: currentUser.id,
              sessionId,
            }),
          );
          if (!wasDeleted) {
            setMissingDataFileError(`Failed to remove "${file.fileName}". Please try again.`);
            setDeletingMissingDataFile(null);
            return;
          }
        } catch {
          setMissingDataFileError(`Failed to remove "${file.fileName}". Please try again.`);
          setDeletingMissingDataFile(null);
          return;
        }
        setDeletingMissingDataFile(null);
      }

      setMissingDataFiles((current) =>
        current.filter((candidate) => candidate.fileName !== file.fileName),
      );
    };
    const missingFields = [
      ...stringArrayFromDetails(interrupt.details, 'missing_profile_labels'),
      ...stringArrayFromDetails(interrupt.details, 'missing_domain_labels'),
    ];
    const alreadyHave = stringArrayFromDetails(interrupt.details, 'already_have_labels');
    // "Use mock data" moves up into the header action slot; it fires the same
    // choice: 'mock' resume the old footer "Mock it" button did.
    const mockAction = (
      <button
        type="button"
        className="interrupt-card-secondary"
        disabled={isResumeInFlight || hasSelectedFiles}
        onClick={() => onSubmit({ ...base, kind: 'missing-data', choice: 'mock' })}
      >
        <DatabaseOutlined aria-hidden /> Use mock data
      </button>
    );
    return shell(
      <>
        {missingFields.length > 0 && (
          <div className="interrupt-card-chips">
            {missingFields.map((label) => (
              <span key={label} className="interrupt-card-chip">
                {label}
              </span>
            ))}
          </div>
        )}
        {alreadyHave.length > 0 && (
          <div className="interrupt-card-captured">Already captured: {alreadyHave.join(', ')}</div>
        )}
        {renderTextarea(
          hasSelectedFiles
            ? 'Remove attached files to enter details…'
            : 'Fill in the missing details…',
          submitFill,
          isResumeInFlight || hasSelectedFiles,
        )}
        <div className="interrupt-card-file-section">
          <div className="interrupt-card-file-divider" aria-hidden="true">
            <span>or</span>
          </div>
          <input
            ref={missingDataFileInputRef}
            className="interrupt-card-file-input"
            type="file"
            multiple
            accept={ALLOWED_FILE_EXTENSIONS.join(',')}
            aria-label="Attach source documents"
            disabled={isResumeInFlight || isUploadingFiles || draft.length > 0}
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void addMissingDataFiles(files);
            }}
          />
          <button
            type="button"
            className="interrupt-card-file-button"
            disabled={isResumeInFlight || isUploadingFiles || draft.length > 0}
            onClick={() => missingDataFileInputRef.current?.click()}
          >
            <FileAddOutlined aria-hidden /> Attach source documents
          </button>
          {missingDataFiles.length > 0 && (
            <div className="interrupt-card-file-list" aria-label="Attached source documents">
              {missingDataFiles.map((file) => (
                <div
                  key={file.fileName}
                  className={`interrupt-card-file-item ${
                    file.error ? 'error' : file.uploaded ? 'uploaded' : 'uploading'
                  }`}
                >
                  <FileTextOutlined aria-hidden />
                  <span className="interrupt-card-file-name">{file.fileName}</span>
                  <span className="interrupt-card-file-status">
                    {file.uploading
                      ? 'Uploading…'
                      : file.error
                        ? 'Upload failed'
                        : file.uploaded
                          ? 'Ready'
                          : 'Preparing…'}
                  </span>
                  <button
                    type="button"
                    className="interrupt-card-file-remove"
                    aria-label={`Remove ${file.fileName}`}
                    disabled={file.uploading || deletingMissingDataFile !== null}
                    onClick={() => void removeMissingDataFile(file)}
                  >
                    <DeleteOutlined aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          )}
          {missingDataFileError && (
            <div className="interrupt-card-error" role="alert">
              {missingDataFileError}
            </div>
          )}
        </div>
        <div className="interrupt-card-actions">
          <button
            type="button"
            className="interrupt-card-submit"
            disabled={!canFill}
            onClick={submitFill}
          >
            {isUploadingFiles ? 'Uploading…' : submitLabel}
          </button>
        </div>
      </>,
      { headerAction: mockAction },
    );
  }

  // source-conflict: pick one of the competing claims, or type a different value.
  // Pill selection and typing are mutually exclusive (see renderOptions/
  // renderTextarea), so only one is ever set; the draft-first check below is a
  // defensive tiebreaker. weave-x transforms these agreed fields.
  // TODO(ENG-2714): confirm with the owner that 'other' supersedes
  // 'existing_claim' if both were ever populated.
  if (interrupt.kind === 'source-conflict') {
    const canSubmit = !isResumeInFlight && (draft.length > 0 || selectedOptionId !== null);
    const conflictId =
      typeof interrupt.details?.conflict_id === 'string'
        ? interrupt.details.conflict_id
        : undefined;
    const conflictSignature =
      typeof interrupt.details?.conflict_signature === 'string'
        ? interrupt.details.conflict_signature
        : undefined;
    const sourceConflictMeta = {
      ...(conflictId ? { conflictId } : {}),
      ...(conflictSignature ? { conflictSignature } : {}),
    };
    const submit = () => {
      if (!canSubmit) return;
      const resume: InterruptResume =
        draft.length > 0
          ? {
              ...base,
              kind: 'source-conflict',
              ...sourceConflictMeta,
              choiceKind: 'other',
              otherValue: draft,
            }
          : {
              ...base,
              kind: 'source-conflict',
              ...sourceConflictMeta,
              choiceKind: 'existing_claim',
              selectedClaimId: selectedOptionId ?? undefined,
            };
      onSubmit(resume);
    };
    return shell(
      <>
        {options.length > 0 && renderConflictRows()}
        {renderTextarea('Or enter a different value…')}
        <div className="interrupt-card-actions">
          <button
            type="button"
            className="interrupt-card-submit"
            disabled={!canSubmit}
            onClick={submit}
          >
            {submitLabel}
          </button>
        </div>
      </>,
    );
  }

  // approval / generic: an option id (approval) or free text (generic), as before.
  const kind = interrupt.kind;
  const hasOptions = options.length > 0;
  const canSubmit =
    !isResumeInFlight && (hasOptions ? selectedOptionId !== null : draft.length > 0);
  const submit = () => {
    if (!canSubmit) return;
    const resume: InterruptResume = hasOptions
      ? { ...base, kind, choice: selectedOptionId ?? undefined }
      : { ...base, kind, answer: draft };
    onSubmit(resume);
  };
  return shell(
    <>
      {hasOptions ? renderOptions() : renderTextarea('Type your answer…', submit)}
      <div className="interrupt-card-actions">
        <button
          type="button"
          className="interrupt-card-submit"
          disabled={!canSubmit}
          onClick={submit}
        >
          {submitLabel}
        </button>
      </div>
    </>,
  );
}

function ConversationPanelBody({
  useV2LoadingIndicator = false,
  scrollbarSuppressedUntil,
  onInterruptSubmit,
}: {
  useV2LoadingIndicator?: boolean;
  scrollbarSuppressedUntil?: number;
  onInterruptSubmit: (resume: InterruptResume) => void;
}) {
  const { conversation, isLoading, isWaitingForResponse, progressPhase } = useAppSelector(
    (state) => state.canvas,
  );
  const hasPendingInterrupt = useAppSelector(selectHasPendingInterrupt);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [analyzingMessageIndex, setAnalyzingMessageIndex] = useState(0);

  // When a streaming-progress phase has been derived from v4 graph events, show
  // that phase's messages; otherwise fall back to the generic analyzing copy.
  const statusMessages =
    useV2LoadingIndicator && progressPhase !== null
      ? PHASE_LABELS[progressPhase]
      : ANALYZING_STATUS_MESSAGES;

  useEffect(() => {
    if (!useV2LoadingIndicator || (!isWaitingForResponse && !isLoading)) return;
    setAnalyzingMessageIndex(0);
    const interval = setInterval(() => {
      setAnalyzingMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [useV2LoadingIndicator, isWaitingForResponse, isLoading, statusMessages]);

  useEffect(() => {
    if (conversation.messages.length === 0) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation.messages]);

  // The interrupt card does not add a message, so bring it into view on its own
  // when it appears at the bottom of the thread.
  useEffect(() => {
    if (hasPendingInterrupt && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasPendingInterrupt]);

  useEffect(() => {
    if (scrollbarSuppressedUntil != null && scrollbarSuppressedUntil > 0) {
      setShowScrollbar(false);
      if (scrollbarTimeoutRef.current) {
        clearTimeout(scrollbarTimeoutRef.current);
        scrollbarTimeoutRef.current = null;
      }
    }
  }, [scrollbarSuppressedUntil]);

  const scheduleScrollbarHide = useCallback(
    (nowOrEvent?: number | Event) => {
      const t = typeof nowOrEvent === 'number' ? nowOrEvent : Date.now();
      if (
        scrollbarSuppressedUntil != null &&
        scrollbarSuppressedUntil > 0 &&
        t < scrollbarSuppressedUntil
      ) {
        return;
      }
      if (scrollbarTimeoutRef.current) {
        clearTimeout(scrollbarTimeoutRef.current);
      }
      setShowScrollbar(true);
      scrollbarTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
        scrollbarTimeoutRef.current = null;
      }, 800);
    },
    [scrollbarSuppressedUntil],
  );

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', scheduleScrollbarHide);
    el.addEventListener('wheel', scheduleScrollbarHide, { passive: true });
    return () => {
      el.removeEventListener('scroll', scheduleScrollbarHide);
      el.removeEventListener('wheel', scheduleScrollbarHide);
      if (scrollbarTimeoutRef.current) {
        clearTimeout(scrollbarTimeoutRef.current);
      }
    };
  }, [scheduleScrollbarHide, conversation.messages.length]);

  return (
    <div className="conversation-panel-body">
      <PdfProcessingProgress />
      {conversation.messages.length === 0 ? (
        <ConversationIntroSection />
      ) : (
        <div
          ref={messagesContainerRef}
          className={`conversation-messages ${showScrollbar ? 'show-scrollbar' : ''}`}
        >
          {conversation.messages.map((message, index) => (
            <div className="conversation-message-item" key={index}>
              {message.sender === 'ai' && (
                <div className="conversation-messages-ai-name">Weave X</div>
              )}
              {message.sender === 'tool' && (
                <div className="conversation-messages-ai-name">Weave X</div>
              )}
              <div className={`message ${message.sender}`}>
                <div className="message-content">
                  {renderMessageContent(message.content, message.sender)}
                </div>
              </div>
            </div>
          ))}
          {(isWaitingForResponse || isLoading) &&
            (useV2LoadingIndicator ? (
              <div
                className="conversation-analyzing-indicator"
                role="status"
                aria-live="polite"
                aria-busy
              >
                <AnalyzingBulbIcon className="analyzing-bulb-icon" aria-hidden />
                <span>{statusMessages[analyzingMessageIndex % statusMessages.length]}</span>
              </div>
            ) : (
              <div className="loading-container">
                <p>Weave X</p>
                <ChatThinkingAnimation />
              </div>
            ))}
          {hasPendingInterrupt && <InterruptCard onSubmit={onInterruptSubmit} />}
          <div ref={messagesEndRef}></div>
        </div>
      )}
    </div>
  );
}

type ChatInputVariant = 'default' | 'v2';

const WEB_SEARCH_DESCRIPTION =
  'Allow Wavvest AI to search recent web information when answering this prompt.';
const EMPTY_PROMPT_SEND_HELP_TEXT = 'Enter a prompt to send.';

function ConversationPanelFooter({
  variant = 'default',
  prefilledInputValue,
  prefilledFullInputValue,
  prefilledInputEventId,
  prefilledGuidanceId,
  plannerMode,
  plannerAgentId,
  v2ComposerNotice,
  onPlannerModeChange,
  onPlannerModeRequired,
  requirePlannerModeBeforeFirstSend = false,
  v2ToolbarStartContent,
  footerAddon,
}: {
  variant?: ChatInputVariant;
  prefilledInputValue?: string;
  prefilledFullInputValue?: string;
  prefilledInputEventId?: number;
  prefilledGuidanceId?: string;
  plannerMode?: PlannerMode | null;
  plannerAgentId?: AgentId;
  v2ComposerNotice?: React.ReactNode;
  onPlannerModeChange?: (mode: PlannerMode) => void;
  onPlannerModeRequired?: () => void;
  requirePlannerModeBeforeFirstSend?: boolean;
  v2ToolbarStartContent?: React.ReactNode;
  footerAddon?: React.ReactNode;
}) {
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled();
  const footerPlannerMode = requirePlannerModeBeforeFirstSend
    ? (plannerMode ?? null)
    : (plannerMode ?? DEFAULT_PLANNER_MODE);
  const disclaimer = (
    <p className="conversation-panel-footer-disclaimer">
      Wavvest AI can make mistakes. Always review outputs.
    </p>
  );
  const isV2Footer = variant === 'v2';

  return (
    <div className="conversation-panel-footer">
      <div className="conversation-panel-footer-input-wrap">
        <ChatInput
          variant={variant}
          firmDataEnabled={firmDataEnabled}
          firmDataLoading={firmDataLoading}
          prefilledInputValue={prefilledInputValue}
          prefilledFullInputValue={prefilledFullInputValue}
          prefilledInputEventId={prefilledInputEventId}
          prefilledGuidanceId={prefilledGuidanceId}
          plannerMode={footerPlannerMode}
          plannerAgentId={plannerAgentId}
          v2ComposerNotice={v2ComposerNotice}
          onPlannerModeChange={onPlannerModeChange}
          onPlannerModeRequired={onPlannerModeRequired}
          requirePlannerModeBeforeFirstSend={requirePlannerModeBeforeFirstSend}
          v2ToolbarStartContent={v2ToolbarStartContent}
          afterInputContent={isV2Footer ? disclaimer : undefined}
        />
        {!isV2Footer && disclaimer}
        {footerAddon}
      </div>
      <ContextAttachmentList firmDataEnabled={firmDataEnabled} />
    </div>
  );
}

function ConversationIntroSection() {
  const dispatch = useAppDispatch();
  const webSearchEnabled = useAppSelector(selectWebSearchEnabled);
  const user = useAppSelector(selectUser);
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled();
  const suggestions = useMemo(() => {
    return [
      'Generate a financial plan for a 30-year-old',
      'Create a pie chart showing asset allocation',
      'Visualize retirement savings projection over 30 years',
      'Create a bar chart showing monthly expenses',
      'Generate a retirement savings projection for a 50-year-old',
      'Draw a scatter plot showing stock returns vs. market indices',
      'Create a expense/income breakdown for a family of 4',
    ];
  }, []);

  const getSuggestionIcon = (suggestion: string) => {
    if (suggestion.includes('pie chart') || suggestion.includes('asset allocation')) {
      return <PieChartOutlined />;
    } else if (suggestion.includes('bar chart') || suggestion.includes('expenses')) {
      return <BarChartOutlined />;
    } else if (suggestion.includes('scatter plot') || suggestion.includes('projection')) {
      return <LineChartOutlined />;
    } else if (suggestion.includes('financial plan') || suggestion.includes('income')) {
      return <DollarOutlined />;
    } else {
      return <BarChartOutlined />;
    }
  };
  const [pickedSuggestions, setPickedSuggestions] = useState<string[]>([]);

  useEffect(() => {
    setPickedSuggestions(suggestions.sort(() => Math.random() - 0.5).slice(0, 3));
  }, [suggestions]);

  const handleSuggestionClick = async (suggestion: string) => {
    try {
      const additionalContextJson = store.getState().canvas.conversation.additionalContextJson;
      const plannerProfileResolution = await resolvePlannerProfileSelectionForSession({
        firmDataEnabled,
        firmDataLoading,
        plannerInvestorProfile: additionalContextJson?.plannerInvestorProfile,
        additionalContextJson,
        dispatch,
        user,
      });
      if (plannerProfileResolution.status === 'blocked') {
        message[plannerProfileResolution.level](plannerProfileResolution.message);
        return;
      }
      const plannerProfileSelection = plannerProfileResolution.selection;
      const sendAdditionalContextJson = firmDataEnabled
        ? additionalContextJson
        : additionalContextWithoutPlannerProfile(additionalContextJson);

      await canvasWebSocket.createNewSession(plannerProfileSessionOptions(plannerProfileSelection));
      const sessionId = canvasWebSocket.getSessionId();

      // Auto-assign thread to investor if selected (Sandbox Mode)
      const selectedInvestor =
        store.getState().planning.guidedMode.investorSelector.selectedInvestor;
      if (selectedInvestor && sessionId) {
        try {
          await createSessionAssociation({
            investorId: selectedInvestor.id,
            cognitoId: selectedInvestor.cognitoId,
            threadId: sessionId,
            title: 'Planning Session',
            description: '',
            notes: '',
          });
        } catch (e) {
          console.warn('Auto-assign failed', e);
        }
      }

      dispatch(addUserMessage(suggestion));
      const sent = canvasWebSocket.sendVisualizationRequest({
        message: suggestion,
        files: [],
        additionalContext: additionalContextForPlannerRequest(sendAdditionalContextJson),
        webSearchEnabled: webSearchEnabled,
        clientId: plannerProfileSelection?.firmId,
        investorId: plannerProfileSelection?.investorId,
      });
      if (sent && plannerProfileSelection) {
        dispatch(
          updateConversationAdditionalContextJson(
            retainedPlannerProfileContext(
              {
                ...(additionalContextJson || {}),
                plannerInvestorProfile: JSON.stringify(plannerProfileSelection),
              },
              { submitted: true },
            ),
          ),
        );
      } else if (!firmDataEnabled && plannerProfileResolution.candidate) {
        dispatch(
          updateConversationAdditionalContextJson(
            additionalContextWithoutPlannerProfile(additionalContextJson),
          ),
        );
      }
    } catch (e) {
      message.error('Failed to send message, please try again later');
      console.error(e);
    }
  };

  const handleRefreshSuggestions = () => {
    setPickedSuggestions(suggestions.sort(() => Math.random() - 0.5).slice(0, 3));
  };

  return (
    <div className="conversation-intro-section">
      <h4>I can help you turn data into clean, actionable visuals.</h4>
      <p>Just tell me what you need and I'll build it!</p>
      <div className="suggestion-container">
        {pickedSuggestions.map((suggestion) => (
          <div className="suggestion-item" key={suggestion}>
            <button onClick={() => handleSuggestionClick(suggestion)}>
              <div className="suggestion-content">
                <div className="suggestion-text">{suggestion}</div>
                <div className="suggestion-icon">{getSuggestionIcon(suggestion)}</div>
              </div>
            </button>
          </div>
        ))}
        <a className="more-suggestions-link" onClick={handleRefreshSuggestions}>
          <BulbOutlined />
          More suggestions
        </a>
      </div>
    </div>
  );
}

function ChatInput({
  variant = 'default',
  firmDataEnabled,
  firmDataLoading,
  prefilledInputValue,
  prefilledFullInputValue,
  prefilledInputEventId,
  prefilledGuidanceId,
  plannerMode,
  plannerAgentId,
  v2ComposerNotice,
  onPlannerModeChange,
  onPlannerModeRequired,
  requirePlannerModeBeforeFirstSend = false,
  v2ToolbarStartContent,
  afterInputContent,
}: {
  variant?: ChatInputVariant;
  firmDataEnabled: boolean;
  firmDataLoading: boolean;
  prefilledInputValue?: string;
  prefilledFullInputValue?: string;
  prefilledInputEventId?: number;
  prefilledGuidanceId?: string;
  plannerMode: PlannerMode | null;
  plannerAgentId?: AgentId;
  v2ComposerNotice?: React.ReactNode;
  onPlannerModeChange?: (mode: PlannerMode) => void;
  onPlannerModeRequired?: () => void;
  requirePlannerModeBeforeFirstSend?: boolean;
  v2ToolbarStartContent?: React.ReactNode;
  afterInputContent?: React.ReactNode;
}) {
  const {
    conversation,
    isLoading,
    currentThreadId,
    isWaitingForResponse,
    activeRunId,
    isCancellingRun,
    isLegacySession,
  } = useAppSelector((state) => state.canvas);
  const webSearchEnabled = useAppSelector(selectWebSearchEnabled);
  const user = useAppSelector(selectUser);
  const checkpointRefreshToken = useAppSelector((state) => state.canvas.checkpointRefreshToken);
  const hasPendingInterrupt = useAppSelector(selectHasPendingInterrupt);
  const guidedSelectedInvestor = useAppSelector(
    (state) => state.planning.guidedMode.investorSelector.selectedInvestor,
  );
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState('');
  const [pendingFullPrompt, setPendingFullPrompt] = useState<string | null>(null);
  // Tracks the guidanceId of the suggested prompt currently prefilled in the
  // input, so it can be sent with the message. Follows the same lifecycle as
  // pendingFullPrompt: set on prefill, cleared on manual edit or after send.
  const [pendingGuidanceId, setPendingGuidanceId] = useState<string | null>(null);
  const [showToolsSubmenu, setShowToolsSubmenu] = useState(false);
  const [isPlannerModePromptOpen, setIsPlannerModePromptOpen] = useState(false);
  const uploadCreatedSessionRef = useRef<{
    sessionId: string;
    agentId?: AgentId;
  } | null>(null);
  const plannerSessionOptions = useCallback(
    (selection: PlannerInvestorProfileSelection | null) => {
      const profileOptions = plannerProfileSessionOptions(selection);
      if (!profileOptions && !plannerAgentId) return undefined;

      return {
        ...(profileOptions ?? {}),
        ...(plannerAgentId ? { agentId: plannerAgentId } : {}),
      };
    },
    [plannerAgentId],
  );
  const [sendAfterPlannerModeSelect, setSendAfterPlannerModeSelect] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const latestConversationRef = useRef(conversation);
  const latestGuidedSelectedInvestorRef = useRef(guidedSelectedInvestor);
  const showWsConnectionFailedModal = useWsConnectionGuard();

  latestConversationRef.current = conversation;
  latestGuidedSelectedInvestorRef.current = guidedSelectedInvestor;

  const [checkpoints, setCheckpoints] = useState<AgentCheckpoint[]>([]);
  const [currentCheckpointIndex, setCurrentCheckpointIndex] = useState<number | null>(null);
  const [checkpointsLoading, setCheckpointsLoading] = useState(false);
  const [checkpointSwitchLoading, setCheckpointSwitchLoading] = useState(false);
  const checkpointsRequestIdRef = useRef(0);
  const checkpointRefreshTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const activeCheckpointIdRef = useRef<string | null>(null);

  // Tool modals state
  const [isPlaidDataModalOpen, setIsPlaidDataModalOpen] = useState(false);
  const [isMeetingNotesModalOpen, setIsMeetingNotesModalOpen] = useState(false);
  const [isApexDataModalOpen, setIsApexDataModalOpen] = useState(false);
  const [isInvestorProfileModalOpen, setIsInvestorProfileModalOpen] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const apexEnabled = useAppSelector(selectApexEnabled);

  const activeCheckpoint =
    currentCheckpointIndex !== null ? (checkpoints[currentCheckpointIndex] ?? null) : null;

  const hasCheckpointControls = checkpoints.length > 1;
  const canNavigateOlder = currentCheckpointIndex !== null && currentCheckpointIndex > 0;
  const canNavigateNewer =
    currentCheckpointIndex !== null && currentCheckpointIndex < checkpoints.length - 1;
  const checkpointLabel =
    currentCheckpointIndex !== null
      ? `${currentCheckpointIndex + 1}/${checkpoints.length}${
          currentCheckpointIndex === checkpoints.length - 1 ? ' Latest' : ''
        }`
      : '--/--';
  // Freeze checkpoint navigation while an interrupt is pending: switching
  // checkpoints swaps the conversation/visualization but never clears
  // pendingInterrupt, which would strand the interrupt card (and its stale resume
  // token) on top of a different run. Mirrors the composer being disabled here.
  const isCheckpointActionDisabled =
    isLoading || isWaitingForResponse || checkpointSwitchLoading || hasPendingInterrupt;
  const disableNavigateOlder =
    !canNavigateOlder || isCheckpointActionDisabled || checkpointsLoading;
  const disableNavigateNewer =
    !canNavigateNewer || isCheckpointActionDisabled || checkpointsLoading;

  const activeCheckpointId = activeCheckpoint?.checkpoint_id ?? null;
  const currentPlannerProfileSelection = useMemo(
    () =>
      firmDataEnabled
        ? plannerInvestorProfileSelectionFromSources(
            conversation.additionalContextJson?.plannerInvestorProfile,
          )
        : null,
    [conversation.additionalContextJson?.plannerInvestorProfile, firmDataEnabled],
  );
  const hasSendableContent =
    inputValue.trim().length > 0 ||
    (pendingFullPrompt != null && String(pendingFullPrompt).trim() !== '') ||
    Boolean(conversation.contextFiles?.some((file) => file.uploaded && file.fileKey)) ||
    currentPlannerProfileSelection !== null;
  const isPlannerModeRequiredForFirstSend =
    variant === 'v2' &&
    requirePlannerModeBeforeFirstSend &&
    conversation.messages.length === 0 &&
    plannerMode === null;

  // A run is started over the websocket; activeRunId is only assigned once the
  // run_metadata event arrives. If it never arrives (lost/failed run start) the
  // composer would freeze forever with no runId to cancel. After a grace period
  // spent waiting with no runId, mark the run recoverable so the stop button can
  // locally clear the stuck state. The timer is torn down (and eligibility reset)
  // the instant a runId arrives or we stop waiting, so a run that is merely in
  // its normal startup window is never treated as stuck.
  const [stuckRecoveryEligible, setStuckRecoveryEligible] = useState(false);
  const awaitingRunStart =
    activeRunId === null && (isWaitingForResponse || isLoading) && !hasPendingInterrupt;
  useEffect(() => {
    if (!awaitingRunStart) {
      setStuckRecoveryEligible(false);
      return;
    }
    const timer = window.setTimeout(
      () => setStuckRecoveryEligible(true),
      STUCK_RUN_RECOVERY_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [awaitingRunStart]);

  // The run stays active (checkpointed) during an interruption, but the send
  // button must read as "you need to answer", not "you can stop the run", so the
  // cancel affordance is suppressed while an interrupt is pending.
  const { isRunActive, showCancelButton, sendButtonDisabled } = deriveComposerRunControls({
    activeRunId,
    isLoading,
    isWaitingForResponse,
    isCancellingRun,
    hasPendingInterrupt,
    isLegacySession,
    checkpointSwitchLoading,
    hasSendableContent,
    stuckRecoveryEligible,
  });

  const inputControlsDisabled =
    isLegacySession ||
    checkpointSwitchLoading ||
    isRunActive ||
    isCancellingRun ||
    hasPendingInterrupt;
  const sendButtonLabel = isLegacySession
    ? 'Legacy sessions are read-only'
    : hasPendingInterrupt
      ? 'Answer the question above to continue'
      : showCancelButton
        ? 'Cancel response'
        : 'Send Message';
  const inputPlaceholder = isLegacySession
    ? 'Legacy sessions are read-only'
    : variant === 'v2'
      ? 'Describe a client situation or what you want to plan...'
      : `For example: "Generate a financial plan for a 30-year-old"`;
  const isV2Input = variant === 'v2';
  const hasConversationStarted = conversation.messages.length > 0;
  const shouldRenderV2ToolbarWebSearch = isV2Input && !hasConversationStarted;
  const shouldRenderV2Toolbar =
    isV2Input && (Boolean(v2ToolbarStartContent) || shouldRenderV2ToolbarWebSearch);

  useEffect(() => {
    if (
      isPlannerModeRequiredForFirstSend &&
      !inputControlsDisabled &&
      !showCancelButton &&
      !sendButtonDisabled
    ) {
      return;
    }

    setIsPlannerModePromptOpen(false);
    setSendAfterPlannerModeSelect(false);
  }, [
    inputControlsDisabled,
    isPlannerModeRequiredForFirstSend,
    sendButtonDisabled,
    showCancelButton,
  ]);

  useEffect(() => {
    activeCheckpointIdRef.current = activeCheckpointId;
  }, [activeCheckpointId]);

  useEffect(() => {
    if (prefilledInputValue === undefined) return;
    setInputValue(prefilledInputValue);
    setPendingFullPrompt(prefilledFullInputValue ?? null);
    setPendingGuidanceId(prefilledGuidanceId ?? null);

    // Move cursor to end so users can continue typing immediately.
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      const len = prefilledInputValue.length;
      textareaRef.current.setSelectionRange(len, len);
    });
  }, [prefilledInputValue, prefilledFullInputValue, prefilledInputEventId, prefilledGuidanceId]);

  useEffect(() => {
    if (inputControlsDisabled && showToolsSubmenu) {
      setShowToolsSubmenu(false);
    }
  }, [inputControlsDisabled, showToolsSubmenu]);

  const fetchCheckpoints = useCallback(
    async (options?: { resetToLatest?: boolean }) => {
      const requestId = ++checkpointsRequestIdRef.current;

      if (!currentThreadId) {
        setCheckpoints([]);
        setCurrentCheckpointIndex(null);
        setCheckpointsLoading(false);
        activeCheckpointIdRef.current = null;
        return;
      }

      const shouldResetToLatest = Boolean(options?.resetToLatest);

      setCheckpointsLoading(true);

      try {
        const result = await getResumableCheckpoints(currentThreadId);
        if (checkpointsRequestIdRef.current !== requestId) {
          return;
        }

        // Normalize to [oldest, ..., newest] so 1/2 = beginning, N/N = end (Latest)
        const ordered = result.slice().reverse();
        setCheckpoints(ordered);

        if (result.length === 0) {
          setCurrentCheckpointIndex(null);
          activeCheckpointIdRef.current = null;
          return;
        }

        const preservedCheckpointId = shouldResetToLatest ? null : activeCheckpointIdRef.current;

        let nextIndex: number;
        if (shouldResetToLatest) {
          nextIndex = ordered.length - 1; // latest = end of chat
        } else if (preservedCheckpointId) {
          const preservedIndex = ordered.findIndex(
            (checkpoint) => checkpoint.checkpoint_id === preservedCheckpointId,
          );
          nextIndex = preservedIndex !== -1 ? preservedIndex : ordered.length - 1;
        } else {
          nextIndex = ordered.length - 1;
        }

        setCurrentCheckpointIndex(nextIndex);
        activeCheckpointIdRef.current = ordered[nextIndex]?.checkpoint_id ?? null;
      } catch (error) {
        if (checkpointsRequestIdRef.current === requestId) {
          console.error('Failed to load checkpoints:', error);
        }
      } finally {
        if (checkpointsRequestIdRef.current === requestId) {
          setCheckpointsLoading(false);
        }
      }
    },
    [currentThreadId],
  );

  useEffect(() => {
    void fetchCheckpoints({ resetToLatest: true });
  }, [fetchCheckpoints]);

  useEffect(() => {
    if (!checkpointRefreshToken || !currentThreadId) {
      return;
    }

    const timerId = window.setTimeout(() => {
      void fetchCheckpoints({ resetToLatest: true });
      checkpointRefreshTimerRef.current = null;
    }, 2000);

    checkpointRefreshTimerRef.current = timerId;

    return () => {
      window.clearTimeout(timerId);
    };
  }, [checkpointRefreshToken, currentThreadId, fetchCheckpoints]);

  const handleCheckpointNavigate = useCallback(
    async (direction: 'previous' | 'next') => {
      if (currentCheckpointIndex === null || !currentThreadId || checkpoints.length === 0) {
        return;
      }

      const offset = direction === 'previous' ? -1 : 1;
      const nextIndex = currentCheckpointIndex + offset;

      if (nextIndex < 0 || nextIndex >= checkpoints.length) {
        return;
      }

      const targetCheckpoint = checkpoints[nextIndex];
      if (!targetCheckpoint) {
        return;
      }

      setCheckpointSwitchLoading(true);

      try {
        const [checkpointMessages, checkpointState] = await Promise.all([
          fetchChatHistoryOnCheckpoint(currentThreadId, targetCheckpoint),
          fetchThreadStateOnCheckpoint(currentThreadId, targetCheckpoint),
        ]);

        const messages = replaceSuggestedPromptsWithNames(
          mapAgentMessagesToConversation(checkpointMessages),
        );

        dispatch(
          setConversation({
            messages,
          }),
        );
        dispatch(setIsWaitingForResponse(false));
        dispatch(setIsLoading(false));
        dispatch(resetProgressState());

        const visualizationHtml = extractVisualizationHtmlFromState(checkpointState);

        dispatch(
          updateVisualization({
            sourceCode: visualizationHtml,
          }),
        );

        setCurrentCheckpointIndex(nextIndex);
        activeCheckpointIdRef.current = targetCheckpoint.checkpoint_id ?? null;
      } catch (error) {
        console.error('Failed to switch checkpoint:', error);
        antMessage.error('Failed to switch checkpoint');
      } finally {
        setCheckpointSwitchLoading(false);
      }
    },
    [checkpoints, currentCheckpointIndex, currentThreadId, dispatch],
  );

  const MAX_TEXTAREA_LINES = 16;
  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const paddingTop = parseFloat(cs.paddingTop) || 0;
    const paddingBottom = parseFloat(cs.paddingBottom) || 0;
    const borderTop = parseFloat(cs.borderTopWidth) || 0;
    const borderBottom = parseFloat(cs.borderBottomWidth) || 0;
    const minHeight = parseFloat(cs.minHeight) || 0;
    const maxHeight =
      lineHeight * MAX_TEXTAREA_LINES + paddingTop + paddingBottom + borderTop + borderBottom;
    const contentHeight = el.scrollHeight;
    const nextHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = contentHeight <= maxHeight ? 'hidden' : 'auto';
  }, []);

  useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPendingFullPrompt(null);
    setPendingGuidanceId(null);
    setInputValue(e.target.value);
  };

  const handleKeyPress = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (variant === 'v2') return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleSendMessage();
    }
  };

  const handleWebSearchChange = (enabled: boolean) => {
    dispatch(setWebSearchEnabled(enabled));
  };

  const handleWebSearchToggle = () => {
    handleWebSearchChange(!webSearchEnabled);
  };

  const handleToolsLinkClick = () => {
    setShowToolsSubmenu(!showToolsSubmenu);
  };

  const handleCancelRun = async () => {
    if (isCancellingRun) {
      return;
    }

    const sessionId = canvasWebSocket.getSessionId();
    const threadId = sessionId ?? currentThreadId;
    // Read the runId from the live store, not the render-time selector value, so
    // a run_metadata event that lands between render and click is respected —
    // we then cancel the real run over REST instead of taking the local-only
    // recovery path and abandoning a run that just started.
    const runId = store.getState().canvas.activeRunId;

    const resetRunState = () => {
      dispatch(setActiveRunId(null));
      dispatch(setIsWaitingForResponse(false));
      dispatch(setIsLoading(false));
      dispatch(setIsCancellingRun(false));
      dispatch(finalizeToolStatusMessage());
    };

    // Recovery path: with no thread/run to cancel there is nothing to stop on the
    // backend. This is only reachable once the stop button is shown without a
    // runId, which the grace-period gate (stuckRecoveryEligible) allows only
    // after run_metadata has failed to arrive — i.e. the run already finished but
    // its websocket completion event was lost, leaving the loading flags stuck
    // and the composer frozen. Just clear the stuck local state so it unlocks.
    if (!threadId || !runId) {
      resetRunState();
      return;
    }

    dispatch(setIsCancellingRun(true));

    try {
      await cancelThreadRun({ threadId, runId });

      const latestCanvasState = store.getState().canvas;
      const isSameRunContext =
        latestCanvasState.currentThreadId === threadId && latestCanvasState.activeRunId === runId;

      if (isSameRunContext) {
        resetRunState();
      }

      antMessage.success('Message cancelled');
    } catch (error) {
      antMessage.error('Failed to cancel message');
      console.error(error);
    } finally {
      const latestCanvasState = store.getState().canvas;
      const isSameThread = latestCanvasState.currentThreadId === threadId;
      const isSameRun =
        latestCanvasState.activeRunId === runId || latestCanvasState.activeRunId === null;

      if (isSameThread && isSameRun) {
        dispatch(setIsCancellingRun(false));
      }
    }
  };

  const handlePlaidDataClick = () => {
    setIsPlaidDataModalOpen(true);
    setShowToolsSubmenu(false);
  };

  const handleMeetingNotesClick = () => {
    setIsMeetingNotesModalOpen(true);
    setShowToolsSubmenu(false);
  };

  const handleApexDataClick = () => {
    setIsApexDataModalOpen(true);
    setShowToolsSubmenu(false);
  };

  const handleInvestorProfileClick = () => {
    if (!firmDataEnabled) {
      setIsInvestorProfileModalOpen(false);
      setShowToolsSubmenu(false);
      return;
    }
    setIsInvestorProfileModalOpen(true);
    setShowToolsSubmenu(false);
  };

  const addFilesToConversation = async (filesArray: File[]) => {
    // Uploads are blocked while an interruption is pending. Guard at the shared
    // entry so every path (file picker via handleFileUpload, inner chat-input
    // drop) is covered by a single check.
    if (hasPendingInterrupt) {
      return;
    }
    // In guided-v2 pre-chat, use a fresh session for new attachments only when there are
    // no existing context files, so we don't rotate sessions and orphan already-uploaded
    // attachments; if contextFiles already has items, keep the current session.
    const hasExistingContextFiles =
      conversation.contextFiles != null && conversation.contextFiles.length > 0;
    if (variant === 'v2' && conversation.messages.length === 0 && !hasExistingContextFiles) {
      try {
        const currentState = store.getState();
        const currentUser = currentState.userinfo.user;
        const additionalContextJson = currentState.canvas.conversation.additionalContextJson;
        const plannerProfileResolution = await resolvePlannerProfileSelectionForSession({
          firmDataEnabled,
          firmDataLoading,
          plannerInvestorProfile: additionalContextJson?.plannerInvestorProfile,
          additionalContextJson,
          dispatch,
          user: currentUser,
        });
        if (plannerProfileResolution.status === 'blocked') {
          antMessage[plannerProfileResolution.level](plannerProfileResolution.message);
          return;
        }

        if (!canvasWebSocket.getConnectionStatus()) {
          const connected = await canvasWebSocket.connect();
          if (!connected || !canvasWebSocket.getConnectionStatus()) {
            showWsConnectionFailedModal();
            return;
          }
        }
        const sessionOptions = firmDataEnabled
          ? plannerSessionOptions(plannerProfileResolution.selection)
          : plannerSessionOptions(null);
        await canvasWebSocket.createNewSession(sessionOptions);
        const uploadSessionId = canvasWebSocket.getSessionId();
        if (uploadSessionId) {
          uploadCreatedSessionRef.current = {
            sessionId: uploadSessionId,
            agentId: sessionOptions?.agentId,
          };
        }
      } catch {
        antMessage.error('Failed to prepare for file upload. Please try again.');
        return;
      }
    }

    // Use the latest store snapshot to hydrate upload metadata
    const uploadState = store.getState();
    const uploadUser = uploadState.userinfo.user ?? user;
    const currentFiles = uploadState.canvas.conversation.contextFiles || [];
    if (!uploadUser?.id) {
      antMessage.error('Unable to upload files. Please sign in again.');
      return;
    }

    const { contextFiles, errors } = await processFilesForImmediateUpload(
      filesArray,
      uploadUser.id,
      (fileName, updates) => {
        dispatch(updateConversationContextFileStatus({ fileName, updates }));
      },
      currentFiles,
    );

    errors.forEach((error) => {
      antMessage.error(error);
    });

    if (contextFiles.length > 0) {
      dispatch(updateConversationContextFiles([...currentFiles, ...contextFiles]));
      antMessage.success(`${contextFiles.length} file(s) added and uploading...`);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      await addFilesToConversation(Array.from(files));
      e.target.value = '';
    }
  };

  const handleFileButtonClick = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
    setShowToolsSubmenu(false);
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showToolsSubmenu && !target.closest('.chat-input-tools-collection')) {
        setShowToolsSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolsSubmenu]);

  /**
   * @description
   * Send a message to Weave X; this is a handler function to control sending messages logic in the chat input area
   */
  const handleSendMessage = async (modeOverride?: PlannerMode) => {
    if (
      checkpointSwitchLoading ||
      activeRunId !== null ||
      isLoading ||
      isWaitingForResponse ||
      isCancellingRun ||
      isLegacySession ||
      hasPendingInterrupt
    ) {
      return;
    }
    if (!hasSendableContent) return;

    const sendConversation = latestConversationRef.current;
    const resolvedPlannerMode = modeOverride ?? plannerMode;
    if (
      variant === 'v2' &&
      requirePlannerModeBeforeFirstSend &&
      sendConversation.messages.length === 0 &&
      !resolvedPlannerMode
    ) {
      onPlannerModeRequired?.();
      setSendAfterPlannerModeSelect(true);
      setIsPlannerModePromptOpen(true);
      return;
    }

    const effectivePlannerMode = resolvedPlannerMode ?? DEFAULT_PLANNER_MODE;
    const sendGuidedSelectedInvestor = latestGuidedSelectedInvestorRef.current;

    const sendPlannerProfileResolution = await resolvePlannerProfileSelectionForSession({
      firmDataEnabled,
      firmDataLoading,
      plannerInvestorProfile: sendConversation.additionalContextJson?.plannerInvestorProfile,
      additionalContextJson: sendConversation.additionalContextJson,
      dispatch,
      user,
    });
    if (sendPlannerProfileResolution.status === 'blocked') {
      antMessage[sendPlannerProfileResolution.level](sendPlannerProfileResolution.message);
      return;
    }
    const sendPlannerProfileSelection = sendPlannerProfileResolution.selection;
    const sendAdditionalContextJson = sendPlannerProfileSelection
      ? {
          ...(sendConversation.additionalContextJson || {}),
          plannerInvestorProfile: JSON.stringify(sendPlannerProfileSelection),
        }
      : firmDataEnabled
        ? sendConversation.additionalContextJson
        : additionalContextWithoutPlannerProfile(sendConversation.additionalContextJson);
    const hasMessageText = inputValue.trim().length > 0;
    const hasUploadedFilesToSend =
      sendConversation.contextFiles?.some((f) => f.uploaded && f.fileKey) ?? false;
    const profileOnlyPrompt = sendPlannerProfileSelection
      ? plannerInvestorProfilePrompt(sendPlannerProfileSelection)
      : '';

    if (!hasMessageText && !hasUploadedFilesToSend && !profileOnlyPrompt) return;

    // Validate context files before any session creation to avoid orphan sessions
    if (sendConversation.contextFiles) {
      const uploadingFiles = sendConversation.contextFiles.filter((file) => file.uploading);
      const failedFiles = sendConversation.contextFiles.filter((file) => file.error);

      if (uploadingFiles.length > 0) {
        antMessage.warning(`${uploadingFiles.length} file(s) are still uploading. Please wait.`);
        return;
      }

      if (failedFiles.length > 0) {
        antMessage.error(
          `${failedFiles.length} file(s) failed to upload. Please remove and try again.`,
        );
        return;
      }
    }

    if (!canvasWebSocket.getConnectionStatus()) {
      const connected = await canvasWebSocket.connect();
      if (!connected || !canvasWebSocket.getConnectionStatus()) {
        showWsConnectionFailedModal();
        return;
      }
    }

    let sessionId = canvasWebSocket.getSessionId();
    let isNewSession = false;
    const sourceSessionId = sessionId;
    const hasAttachedFiles =
      sendConversation.contextFiles != null && sendConversation.contextFiles.length > 0;
    const uploadedFiles =
      sendConversation.contextFiles?.filter((file) => file.uploaded && file.fileKey) ?? [];
    const uploadCreatedSession =
      uploadCreatedSessionRef.current?.sessionId === sessionId
        ? uploadCreatedSessionRef.current
        : null;
    // In Guided V2 pre-chat, start a new session only when there are no attached files.
    // If the user attached files first, a session was already created for upload — use it
    // so the backend can find the files in the same session.
    const shouldForceNewSessionOnPreChat =
      variant === 'v2' && sendConversation.messages.length === 0 && !hasAttachedFiles;
    let shouldForcePlannerProfileSession =
      sendPlannerProfileSelection !== null &&
      sendConversation.messages.length === 0 &&
      !hasAttachedFiles;
    let rotatedPlannerProfileSession = false;
    if (sendPlannerProfileSelection && sessionId) {
      const currentSessionNeedsPlannerGraph = await shouldStartNewPlannerProfileSession(sessionId);
      if (currentSessionNeedsPlannerGraph === null) {
        antMessage.error('Unable to verify the current planning session. Please try again.');
        return;
      }
      shouldForcePlannerProfileSession =
        shouldForcePlannerProfileSession || currentSessionNeedsPlannerGraph;
      rotatedPlannerProfileSession =
        currentSessionNeedsPlannerGraph && sendConversation.messages.length > 0;
    }
    if (!sessionId || shouldForceNewSessionOnPreChat || shouldForcePlannerProfileSession) {
      // When no session exists, or in Guided V2 pre-chat with no attachments, create a new session.
      try {
        await canvasWebSocket.createNewSession(plannerSessionOptions(sendPlannerProfileSelection));
        sessionId = canvasWebSocket.getSessionId();
        uploadCreatedSessionRef.current = null;
        isNewSession = true;
      } catch (error) {
        antMessage.error('Failed to create new session, please try again later');
        console.error(error);
        return;
      }
    }
    if (
      sendPlannerProfileSelection &&
      shouldForcePlannerProfileSession &&
      uploadedFiles.length > 0
    ) {
      const plannerSessionId = canvasWebSocket.getSessionId();
      const restoreSourceSession = () => {
        if (sourceSessionId) {
          canvasWebSocket.updateSessionId(sourceSessionId);
        }
      };
      if (!sourceSessionId || !plannerSessionId) {
        restoreSourceSession();
        antMessage.error('Failed to prepare attached files for the selected investor profile.');
        return;
      }
      if (!user?.id) {
        restoreSourceSession();
        antMessage.error('Failed to prepare attached files for the selected investor profile.');
        return;
      }
      try {
        const copied = await reattachContextFilesToSession(uploadedFiles, {
          sourceSessionId,
          targetSessionId: plannerSessionId,
          userId: user.id,
        });
        if (!copied) {
          restoreSourceSession();
          antMessage.error('Failed to prepare attached files for the selected investor profile.');
          return;
        }
      } catch (error) {
        restoreSourceSession();
        antMessage.error('Failed to prepare attached files for the selected investor profile.');
        console.error(error);
        return;
      }
    }
    if (rotatedPlannerProfileSession) {
      dispatch(
        setConversation({
          messages: [],
          additionalContextJson: retainedPlannerProfileContext(sendAdditionalContextJson),
        }),
      );
      dispatch(updateVisualization({ sourceCode: '', fileName: '', presignedUrl: '' }));
      dispatch(resetProgressState());
    }

    // Auto-assign thread to investor if new session and investor selected (Sandbox Mode)
    if (isNewSession && sessionId) {
      if (sendGuidedSelectedInvestor?.id) {
        try {
          await createSessionAssociation({
            investorId: sendGuidedSelectedInvestor.id,
            cognitoId: sendGuidedSelectedInvestor.cognitoId,
            threadId: sessionId,
            title: 'Planning Session',
            description: '',
            notes: '',
          });
        } catch (e) {
          console.warn('Auto-assign failed', e);
        }
      }
    }

    // Treat empty/whitespace pendingFullPrompt as absent so inputValue is used
    const effectiveFullPrompt =
      pendingFullPrompt != null && String(pendingFullPrompt).trim() !== ''
        ? pendingFullPrompt
        : null;
    const rawMessage = (effectiveFullPrompt ?? inputValue).trim();
    const messageToSend =
      rawMessage ||
      (hasUploadedFilesToSend ? 'Please use the attached documents.' : profileOnlyPrompt);
    // Show only the prompt name in the dialog when using a suggested prompt; send full prompt to backend
    const messageToDisplay = effectiveFullPrompt
      ? inputValue.trim() || (hasUploadedFilesToSend ? 'Sent with attachments' : '')
      : rawMessage || (hasUploadedFilesToSend ? 'Sent with attachments' : profileOnlyPrompt || '');
    dispatch(addUserMessage(messageToDisplay));
    setInputValue('');
    setPendingFullPrompt(null);
    setPendingGuidanceId(null);

    // Set waiting for response state
    dispatch(setIsWaitingForResponse(true));
    dispatch(setActiveRunId(null));
    dispatch(setIsCancellingRun(false));
    dispatch(resetProgressPhase());

    if (textareaRef.current) {
      textareaRef.current.style.height = '';
    }

    const requestAdditionalContext = additionalContextForPlannerRequest(sendAdditionalContextJson);

    const messageRequestBody: MessageRequestBody = {
      message: messageToSend,
      files: [],
      additionalContext: requestAdditionalContext,
      webSearchEnabled: webSearchEnabled,
      clientId: sendPlannerProfileSelection?.firmId,
      investorId: sendPlannerProfileSelection?.investorId,
    };
    if (
      variant === 'v2' &&
      (sendConversation.messages.length === 0 || isNewSession || rotatedPlannerProfileSession)
    ) {
      messageRequestBody.creativeMode = plannerModeToCreativeMode(effectivePlannerMode);
      const requestAgentId =
        uploadCreatedSession &&
        !isNewSession &&
        !rotatedPlannerProfileSession &&
        uploadedFiles.length > 0
          ? uploadCreatedSession.agentId
          : plannerAgentId;
      if (requestAgentId) {
        messageRequestBody.agentId = requestAgentId;
      }
    }

    // Attach the suggested-prompt guidanceId when one is pending. It shares the
    // pendingFullPrompt lifecycle, so it's only present for an unedited
    // suggested prompt.
    if (pendingGuidanceId) {
      messageRequestBody.guidanceId = pendingGuidanceId;
    }

    if (sendConversation.contextFiles) {
      messageRequestBody.files = uploadedFiles.map((file) => ({
        fileKey: extractLeafContextFileKey(file.fileKey!),
        type: file.type,
      }));

      // Remove sent files from conversation
      dispatch(updateConversationContextFiles([]));
    }

    if (sendAdditionalContextJson) {
      if (requestAdditionalContext) {
        const contextJsonString = JSON.stringify(requestAdditionalContext);
        messageRequestBody.additionalContext = JSON.parse(contextJsonString);
      } else {
        delete messageRequestBody.additionalContext;
      }

      dispatch(
        updateConversationAdditionalContextJson(
          retainedPlannerProfileContext(sendAdditionalContextJson),
        ),
      );
    }

    if (activeCheckpoint) {
      messageRequestBody.checkpoint = activeCheckpoint;
      if (activeCheckpoint.checkpoint_id) {
        messageRequestBody.checkpointId = activeCheckpoint.checkpoint_id;
      }
    }

    const sent = canvasWebSocket.sendVisualizationRequest(messageRequestBody);
    if (!sent) {
      dispatch(setIsWaitingForResponse(false));
      antMessage.error('Failed to send message, please try again later');
      return;
    }

    if (sendPlannerProfileSelection) {
      dispatch(
        updateConversationAdditionalContextJson(
          retainedPlannerProfileContext(sendAdditionalContextJson, { submitted: true }),
        ),
      );
    }
  };

  const handlePlannerModePromptOpenChange = (open: boolean) => {
    setIsPlannerModePromptOpen(open);
    if (!open) {
      setSendAfterPlannerModeSelect(false);
    }
  };

  const handlePlannerModeSelect = async (mode: PlannerMode) => {
    onPlannerModeChange?.(mode);
    setIsPlannerModePromptOpen(false);
    if (!sendAfterPlannerModeSelect) return;

    setSendAfterPlannerModeSelect(false);
    await handleSendMessage(mode);
  };

  const plannerModePromptContent = (
    <div className="first-send-planner-mode-panel" role="group" aria-label="Choose planner mode">
      <div className="first-send-planner-mode-header">
        <div className="first-send-planner-mode-title">Planner mode required</div>
        <button
          type="button"
          className="first-send-planner-mode-close"
          aria-label="Close planner mode prompt"
          onClick={() => handlePlannerModePromptOpenChange(false)}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="first-send-planner-mode-options">
        {(['financial_plan', 'creative'] as PlannerMode[]).map((option) => (
          <Tooltip
            key={option}
            title={PLANNER_MODE_DESCRIPTIONS[option]}
            placement="left"
            color="#101820"
            classNames={{ root: 'planner-mode-description-tooltip' }}
          >
            <button
              type="button"
              className="first-send-planner-mode-option"
              aria-label={PLANNER_MODE_LABELS[option]}
              onClick={() => void handlePlannerModeSelect(option)}
            >
              <span className="first-send-planner-mode-option-icon" aria-hidden="true">
                {option === 'creative' ? <BulbOutlined /> : <DollarOutlined />}
              </span>
              <span>{PLANNER_MODE_LABELS[option]}</span>
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  const disabledSubmitHelperText =
    isV2Input && !showCancelButton && sendButtonDisabled && !inputControlsDisabled
      ? EMPTY_PROMPT_SEND_HELP_TEXT
      : null;
  const sendButtonTitle = disabledSubmitHelperText ? undefined : sendButtonLabel;
  const sendButton = (
    <button
      type="button"
      className={`chat-input-send-button ${
        showCancelButton ? 'chat-input-send-button-cancel' : 'chat-input-send-button-submit'
      }`}
      onClick={showCancelButton ? handleCancelRun : () => void handleSendMessage()}
      aria-label={sendButtonLabel}
      title={sendButtonTitle}
      disabled={sendButtonDisabled}
    >
      {isCancellingRun ? (
        <Spin size="small" />
      ) : showCancelButton ? (
        <StopCircleFilled width={32} height={32} />
      ) : (
        <ArrowUpOutlined />
      )}
    </button>
  );
  const sendButtonWithHelper = disabledSubmitHelperText ? (
    <Tooltip
      title={disabledSubmitHelperText}
      placement="top"
      color="#101820"
      classNames={{ root: 'planner-mode-description-tooltip' }}
    >
      <span className="chat-input-send-button-tooltip-trigger">{sendButton}</span>
    </Tooltip>
  ) : (
    sendButton
  );

  const sendButtonControl =
    isPlannerModeRequiredForFirstSend && !showCancelButton ? (
      <Popover
        open={isPlannerModePromptOpen}
        onOpenChange={handlePlannerModePromptOpenChange}
        trigger={[]}
        placement="topRight"
        overlayClassName="first-send-planner-mode-popover"
        content={plannerModePromptContent}
      >
        {sendButtonWithHelper}
      </Popover>
    ) : (
      sendButtonWithHelper
    );
  const webSearchSwitch = (
    <Tooltip
      title={WEB_SEARCH_DESCRIPTION}
      placement="top"
      color="#101820"
      classNames={{ root: 'planner-mode-description-tooltip' }}
    >
      <button
        type="button"
        role="switch"
        aria-label="Web search"
        aria-checked={webSearchEnabled}
        className={`chat-input-v2-web-search-switch${webSearchEnabled ? ' is-enabled' : ''}`}
        onClick={handleWebSearchToggle}
        disabled={inputControlsDisabled}
      >
        <span className="chat-input-v2-web-search-switch-label">Web search</span>
        <span className="chat-input-v2-web-search-switch-track" aria-hidden>
          <span className="chat-input-v2-web-search-switch-thumb" />
        </span>
      </button>
    </Tooltip>
  );
  const inlineWebSearchButton = (
    <button
      type="button"
      role={isV2Input ? 'switch' : undefined}
      aria-label={isV2Input ? 'Web search' : 'Toggle Web Search'}
      aria-checked={isV2Input ? webSearchEnabled : undefined}
      className={`tools-earth-button${webSearchEnabled ? ' active' : ''}${
        isV2Input ? ' chat-input-v2-inline-web-search-toggle' : ''
      }`}
      onClick={handleWebSearchToggle}
      disabled={inputControlsDisabled}
      title={webSearchEnabled ? 'Web search enabled' : 'Web search disabled'}
    >
      <GlobeV2Icon width={webSearchEnabled ? 20 : 22} height={webSearchEnabled ? 20 : 22} />
      {webSearchEnabled && (
        <>
          <span className="web-search-label">Web Search</span>
          <CloseOutlined className="web-search-close" />
        </>
      )}
    </button>
  );

  return (
    <>
      <div
        className={`chat-input ${isV2Input ? 'chat-input-v2' : ''}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // addFilesToConversation refuses uploads while an interruption is
          // pending (guarded at its definition), so no inline check is needed.
          const files = Array.from(e.dataTransfer.files);
          if (files.length > 0) {
            void addFilesToConversation(files);
          }
        }}
      >
        <div className="chat-input-container">
          {shouldRenderV2Toolbar && (
            <div className="chat-input-v2-toolbar">
              {v2ToolbarStartContent && (
                <div className="chat-input-v2-toolbar-start">{v2ToolbarStartContent}</div>
              )}
              {shouldRenderV2ToolbarWebSearch && (
                <div className="chat-input-v2-toolbar-end">{webSearchSwitch}</div>
              )}
            </div>
          )}
          {isV2Input && v2ComposerNotice && (
            <div className="chat-input-v2-composer-notice">{v2ComposerNotice}</div>
          )}
          <textarea
            ref={textareaRef}
            className="chat-input-textarea"
            placeholder={inputPlaceholder}
            rows={1}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={inputControlsDisabled}
          />

          <div className="chat-input-bottom-container">
            <div className="chat-input-tools-collection">
              <div>
                <button
                  type="button"
                  className={`tools-link-button`}
                  onClick={handleToolsLinkClick}
                  aria-label="Tools Menu"
                  disabled={inputControlsDisabled}
                  title="Access data tools"
                >
                  <LinkV2Icon width={22} height={22} />
                </button>

                {showToolsSubmenu && (
                  <div className="tools-submenu">
                    {firmDataEnabled && (
                      <button className="tools-submenu-item" onClick={handleInvestorProfileClick}>
                        <UserOutlined />
                        <span>Add investor profile</span>
                      </button>
                    )}
                    <button className="tools-submenu-item" onClick={handlePlaidDataClick}>
                      <DatabaseOutlined />
                      <span>Plug in Plaid Data</span>
                    </button>
                    <button className="tools-submenu-item" onClick={handleMeetingNotesClick}>
                      <FileTextOutlined />
                      <span>Attach Meeting Notes</span>
                    </button>
                    {apexEnabled && (
                      <button className="tools-submenu-item" onClick={handleApexDataClick}>
                        <BankOutlined />
                        <span>Attach Apex Data</span>
                      </button>
                    )}
                    <button className="tools-submenu-item" onClick={handleFileButtonClick}>
                      <FileAddOutlined />
                      <span>Add External Files</span>
                    </button>
                  </div>
                )}
              </div>

              {(!isV2Input || hasConversationStarted) && inlineWebSearchButton}
            </div>

            <div className="chat-input-controls">
              {hasCheckpointControls && (
                <div className="chat-input-checkpoint-controls">
                  <button
                    type="button"
                    className="checkpoint-button"
                    onClick={() => handleCheckpointNavigate('previous')}
                    disabled={disableNavigateOlder}
                    title="Earlier checkpoint"
                    aria-label="Earlier checkpoint"
                  >
                    <LeftOutlined />
                  </button>
                  <div className="checkpoint-label">
                    {checkpointsLoading ? <Spin size="small" /> : <span>{checkpointLabel}</span>}
                  </div>
                  <button
                    type="button"
                    className="checkpoint-button"
                    onClick={() => handleCheckpointNavigate('next')}
                    disabled={disableNavigateNewer}
                    title="Later checkpoint"
                    aria-label="Later checkpoint"
                  >
                    <RightOutlined />
                  </button>
                </div>
              )}
              {sendButtonControl}
            </div>
          </div>
        </div>
        {afterInputContent}
        <input
          type="file"
          onChange={handleFileUpload}
          accept={ALLOWED_FILE_EXTENSIONS.join(',')}
          ref={fileRef}
          multiple={true}
          disabled={inputControlsDisabled}
          style={{ display: 'none' }}
        />
      </div>

      {/* Tool modals */}
      {isPlaidDataModalOpen && (
        <PlaidDataModal
          isOpen={isPlaidDataModalOpen}
          onClose={() => setIsPlaidDataModalOpen(false)}
        />
      )}
      {isMeetingNotesModalOpen && (
        <MeetingNotesModal
          isOpen={isMeetingNotesModalOpen}
          onClose={() => setIsMeetingNotesModalOpen(false)}
        />
      )}
      {isApexDataModalOpen && (
        <ApexDataModal isOpen={isApexDataModalOpen} onClose={() => setIsApexDataModalOpen(false)} />
      )}
      {firmDataEnabled && isInvestorProfileModalOpen && (
        <PlannerInvestorProfileModal
          isOpen={isInvestorProfileModalOpen}
          onClose={() => setIsInvestorProfileModalOpen(false)}
        />
      )}
    </>
  );
}

function ContextAttachmentList({ firmDataEnabled }: { firmDataEnabled: boolean }) {
  const { conversation } = useAppSelector((state) => state.canvas);
  const additionalContext = conversation.additionalContextJson || {};
  const hasContextFiles = Boolean(conversation.contextFiles?.length);
  const hasDataContext = hasDataContextItems(additionalContext, firmDataEnabled);

  if (!hasContextFiles && !hasDataContext) {
    return null;
  }

  return (
    <div className="context-attachment-list" aria-label="Attached context">
      <ContextFileList />
      <DataContextList firmDataEnabled={firmDataEnabled} />
    </div>
  );
}

function hasDataContextItems(
  additionalContext: Record<string, unknown>,
  firmDataEnabled: boolean,
): boolean {
  return Boolean(
    additionalContext.plaidData ||
    additionalContext.meetingNotes ||
    additionalContext.apexData ||
    (firmDataEnabled && additionalContext.plannerInvestorProfile),
  );
}

function ContextFileList() {
  const dispatch = useAppDispatch();
  const { conversation } = useAppSelector((state) => state.canvas);

  const contextFiles = conversation.contextFiles;

  if (!contextFiles?.length) {
    return null;
  }

  const handleDeleteFile = async (fileName: string) => {
    const file = contextFiles?.find((f) => f.fileName === fileName);

    if (!file) return;

    // Warn user if file is still uploading
    if (file.uploading) {
      const confirmed = window.confirm(
        `File "${fileName}" is still uploading. Are you sure you want to remove it?`,
      );
      if (!confirmed) return;
    }

    // If file has been uploaded to server, delete it from server first
    if (file.uploaded && file.fileKey) {
      try {
        const { user } = store.getState().userinfo;
        const sessionId = canvasWebSocket.getSessionId();

        if (!sessionId) {
          antMessage.error('No active session found');
          return;
        }

        const fullFileKey = buildFullContextFileKey({
          fileKey: file.fileKey,
          userId: user.id,
          sessionId,
        });

        await deleteContextFile(fullFileKey);
        antMessage.success(`File "${fileName}" deleted successfully`);
      } catch (error) {
        antMessage.error(`Failed to delete file "${fileName}" from server`);
        console.error('Delete file error:', error);
        return; // Don't remove from frontend if server deletion failed
      }
    }

    // Remove from frontend state
    dispatch(removeConversationContextFile(fileName));

    // Show success message for files that weren't uploaded to server
    if (!file.uploaded) {
      antMessage.success(`File "${fileName}" removed`);
    }
  };
  return (
    <div className="context-file-list">
      {contextFiles?.map((file) => {
        const canRemoveFile = !file.submitted || !file.uploaded;

        return (
          <div
            key={file.fileName}
            className={`context-file-list-item ${
              file.uploading ? 'uploading' : file.uploaded ? 'uploaded' : file.error ? 'error' : ''
            }`}
          >
            <Tooltip
              title={file.error ? `${file.fileName} - Error: ${file.error}` : file.fileName}
              placement="bottom"
              color={file.error ? '#ff4d4f' : '#101820'}
            >
              <div className="file-name">
                <span className="context-chip-kind-icon" aria-hidden>
                  <FileTextOutlined />
                </span>
                <span className="file-name-text">{file.fileName}</span>
                {file.uploading && (
                  <span className="file-status-icon">
                    <Spin size="small" style={{ marginRight: 4 }} />
                    <span
                      style={{
                        fontSize: '10px',
                        color: 'var(--theme-text-color)',
                        fontWeight: 500,
                      }}
                    >
                      uploading...
                    </span>
                  </span>
                )}
                {file.error && (
                  <span className="file-status-icon" style={{ color: '#ff4d4f' }}>
                    ✗
                  </span>
                )}
              </div>
            </Tooltip>
            {canRemoveFile && (
              <button
                type="button"
                className="context-chip-remove-button"
                aria-label={`Remove ${file.fileName}`}
                onClick={() => void handleDeleteFile(file.fileName)}
              >
                <DeleteOutlined />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

type PlannerInvestorProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function PlannerInvestorProfileModal({ isOpen, onClose }: PlannerInvestorProfileModalProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const additionalContext = useAppSelector(
    (state) => state.canvas.conversation.additionalContextJson,
  );
  const firmId = userClientIdFromStateUser(user);
  const [options, setOptions] = useState<PlannerInvestorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSearchText('');
    setError(null);
    if (!firmId) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getPlannerInvestorOptions(firmId)
      .then((rows) => {
        if (cancelled) return;
        setOptions(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load planner investor options:', err);
        setError('Investor profiles are unavailable right now.');
        setOptions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [firmId, isOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return options;
    return options.filter((option) => {
      const haystack = [option.displayName, option.status].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [options, searchText]);

  const handleSelect = (option: PlannerInvestorOption) => {
    if (!option.plannerSelectable) {
      return;
    }
    dispatch(
      updateConversationAdditionalContextJson({
        ...(additionalContext || {}),
        plannerInvestorProfile: JSON.stringify({
          firmId: option.firmId,
          investorId: option.investorId,
          displayName: option.displayName,
        }),
        plannerInvestorProfileSubmitted: false,
      }),
    );
    antMessage.success('Investor profile added');
    onClose();
  };

  const columns = [
    {
      title: 'Investor',
      key: 'investor',
      render: (_: unknown, option: PlannerInvestorOption) => (
        <div className="planner-investor-profile-cell">
          <div className="planner-investor-profile-name">{option.displayName}</div>
          {!option.plannerSelectable && option.disabledReason ? (
            <div className="planner-investor-profile-disabled-reason">{option.disabledReason}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 130,
      render: (_: unknown, option: PlannerInvestorOption) => (
        <Button
          type="primary"
          className="planner-brand-action-button"
          size="small"
          icon={<UserOutlined />}
          disabled={!option.plannerSelectable}
          onClick={() => handleSelect(option)}
        >
          Use profile
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Add investor profile"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
      ]}
      width={560}
    >
      <div className="planner-investor-profile-modal">
        {!firmId && (
          <Alert
            type="warning"
            showIcon
            message="Firm context is unavailable for this advisor session."
          />
        )}
        {error && <Alert type="error" showIcon message={error} />}
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search investors"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          className="planner-investor-profile-search"
          disabled={!firmId || loading}
        />
        <Table
          rowKey="investorId"
          columns={columns}
          dataSource={filteredOptions}
          loading={loading}
          pagination={{ pageSize: 6, hideOnSinglePage: true }}
          locale={{
            emptyText: firmId ? 'No investors available' : 'Firm context unavailable',
          }}
          size="middle"
        />
      </div>
    </Modal>
  );
}

type PlaidDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function PlaidDataModal({ isOpen, onClose }: PlaidDataModalProps) {
  const dispatch = useAppDispatch();
  const investors = useAppSelector(selectInvestors);
  const investorsLoading = useAppSelector(selectInvestorsLoadingState) === 'pending';

  // Get pre-selected investor from Redux (if assigned in guided mode)
  const preSelectedInvestor = useAppSelector(
    (state) => state.planning.guidedMode.investorSelector.selectedInvestor,
  );

  // Initialize with pre-selected investor's cognito_id if available
  const getInitialInvestorId = useCallback(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return investor?.cognito_id || null;
    }
    return null;
  }, [preSelectedInvestor, investors]);

  const [selectedInvestor, setSelectedInvestor] = useState<string | null>(getInitialInvestorId);

  // Update selected investor when modal opens with pre-selected investor
  // Reset state when modal closes to ensure proper re-initialization on next open
  useEffect(() => {
    if (isOpen && preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      if (investor?.cognito_id) {
        setSelectedInvestor(investor.cognito_id);
      }
    }
    if (!isOpen) {
      setSelectedInvestor(null);
    }
  }, [isOpen, preSelectedInvestor, investors]);

  const { plaidData, loading: plaidLoading, error } = usePlaidData(selectedInvestor);
  const additionalContext = useAppSelector(
    (state) => state.canvas.conversation.additionalContextJson,
  );

  const existingInvestors = useMemo(
    () => investors.filter((investor) => investor.cognito_id),
    [investors],
  );

  // Check if pre-selected investor is not onboarded (no cognito_id)
  const isPreSelectedNotOnboarded = useMemo(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return Boolean(investor && !investor.cognito_id);
    }
    return false;
  }, [preSelectedInvestor, investors]);

  const preSelectedInvestorName = useMemo(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return investor
        ? getInvestorDisplayName(investor)
        : getInvestorNameOrFallback(preSelectedInvestor.name);
    }
    return '';
  }, [preSelectedInvestor, investors]);

  const renderInvestorOption = (investor: (typeof existingInvestors)[number]) => (
    <div className="investor-option">
      <div className="investor-name">{getInvestorDisplayName(investor)}</div>
      <div className="investor-email">{investor.email}</div>
    </div>
  );

  const renderSelectedInvestorLabel = ({ value }: { value?: React.Key }) => {
    const investor =
      value !== undefined && value !== null
        ? findInvestorByCognitoId(investors, String(value))
        : undefined;
    return investor ? renderInvestorOption(investor) : 'Selected client';
  };

  const handleInvestorSelect = (investorId: string) => {
    setSelectedInvestor(investorId);
  };

  const handleSubmit = () => {
    if (plaidData && selectedInvestor) {
      const selectedInvestorData = findInvestorByCognitoId(investors, selectedInvestor);
      const mergedContext = {
        ...(additionalContext || {}),
      } as AdditionalContextJson;
      dispatch(
        updateConversationAdditionalContextJson({
          ...mergedContext,
          plaidData: JSON.stringify(plaidData),
          plaidInvestor: JSON.stringify({
            id: selectedInvestor,
            name: selectedInvestorData
              ? getInvestorDisplayName(selectedInvestorData)
              : 'Unknown client',
          }),
        }),
      );
      antMessage.success('Plaid data added successfully');
      onClose();
    }
  };

  return (
    <Modal
      title="Select Client for Plaid Data"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          disabled={!plaidData || plaidLoading || isPreSelectedNotOnboarded}
          loading={plaidLoading}
        >
          Add Plaid Data
        </Button>,
      ]}
    >
      <div className="plaid-data-modal-content">
        {isPreSelectedNotOnboarded ? (
          <div
            className="investor-not-onboarded-warning"
            style={{
              padding: '16px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '6px',
              marginBottom: '16px',
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: '8px', color: '#d46b08' }}>
              Client Not Onboarded
            </div>
            <div style={{ color: '#874d00' }}>
              <strong>{preSelectedInvestorName}</strong> has not completed the onboarding process.
              Plaid data is only available for clients who have linked their accounts.
            </div>
            <div style={{ marginTop: '12px', color: '#8c8c8c', fontSize: '13px' }}>
              Please select another client below, or ask this client to complete account linking
              first.
            </div>
          </div>
        ) : null}
        {investorsLoading ? (
          <div className="plaid-data-modal-loading">Loading clients...</div>
        ) : (
          <div className="plaid-data-investor-select">
            <Select
              placeholder="Select a client"
              onChange={handleInvestorSelect}
              value={selectedInvestor || undefined}
              loading={investorsLoading}
              optionFilterProp="label"
              optionLabelProp="label"
              labelRender={renderSelectedInvestorLabel}
              showSearch
              style={{ width: '100%' }}
            >
              {existingInvestors.length > 0
                ? existingInvestors.map((investor) => (
                    <Select.Option
                      key={investor.cognito_id}
                      value={investor.cognito_id}
                      label={getInvestorDisplayName(investor)}
                      title={getInvestorDisplayName(investor)}
                    >
                      {renderInvestorOption(investor)}
                    </Select.Option>
                  ))
                : null}
            </Select>
            {existingInvestors.length === 0 && (
              <div className="no-investors">No clients available</div>
            )}
          </div>
        )}

        {plaidLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading Plaid data...</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <LinkOutlined
                style={{
                  fontSize: 48,
                  color: error.includes('No Plaid data found') ? '#faad14' : '#ff4d4f',
                }}
              />
            </div>
            <h3
              style={{
                color: error.includes('No Plaid data found') ? '#faad14' : '#ff4d4f',
                marginBottom: 8,
              }}
            >
              {error.includes('No Plaid data found')
                ? 'No Plaid Data Available'
                : 'Error Loading Plaid Data'}
            </h3>
            <p style={{ color: '#999', marginBottom: 0 }}>{error}</p>
          </div>
        ) : plaidData ? (
          <div
            style={{
              padding: 16,
              backgroundColor: '#f6f6f6',
              borderRadius: 4,
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <LinkOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              <strong style={{ color: '#52c41a' }}>Plaid Data Ready</strong>
            </div>
            <p style={{ margin: 0, color: '#666' }}>
              {plaidData.accounts?.length > 0
                ? `${plaidData.accounts.length} account(s) loaded`
                : 'Account data loaded'}
              {plaidData.summary ? ' with summary information' : ''}
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

type GroupMode = 'date' | 'investor';
const MAX_HISTORY_TITLE_HYDRATION_REQUESTS = 10;

export function SessionHistoryDrawer({
  open,
  onClose,
  onSessionLoaded,
}: {
  open: boolean;
  onClose: () => void;
  onSessionLoaded?: (
    threadId: string,
    plannerMode: PlannerMode | null,
    plannerEngine: PlannerEngine | null,
  ) => void;
}) {
  const [, setThreads] = useState<Thread[]>([]);
  const [groupedThreads, setGroupedThreads] = useState<ThreadGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingThread, setEditingThread] = useState<Thread | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [assigningThread, setAssigningThread] = useState<Thread | null>(null);
  const [investorModalVisible, setInvestorModalVisible] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | undefined>(undefined);
  const [groupMode, setGroupMode] = useState<GroupMode>('date');
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.userinfo);
  const investors = useAppSelector(selectInvestors);
  const investorsLoadingState = useAppSelector(selectInvestorsLoadingState);

  const getDateGroupLabel = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - today.getDay());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(thisMonthStart);
    lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);

    const threadDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (threadDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (threadDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (threadDate >= thisWeekStart) {
      return 'This Week';
    } else if (threadDate >= lastWeekStart) {
      return 'Last Week';
    } else if (threadDate >= thisMonthStart) {
      return 'This Month';
    } else if (threadDate >= lastMonthStart) {
      return 'Last Month';
    } else {
      return date.getFullYear() === now.getFullYear()
        ? date.toLocaleDateString('en-US', { month: 'long' })
        : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
  };

  const groupThreadsByDate = useCallback(
    (threads: Thread[]): ThreadGroup[] =>
      groupThreads(threads, (thread) => {
        const label = getDateGroupLabel(thread.updated_at);
        return { key: label, label };
      }),
    [],
  );

  const groupThreadsByInvestor = useCallback(
    (threads: Thread[]): ThreadGroup[] =>
      groupThreads(threads, (thread) =>
        getInvestorThreadGroup(thread.metadata?.investorId as string | undefined, investors),
      ),
    [investors],
  );

  const refreshThreads = useCallback(async () => {
    const threads = (await getAllThreads(user.id)) as Thread[];
    const hydratedThreads = await hydrateThreadsWithHistoryTitles(
      threads,
      MAX_HISTORY_TITLE_HYDRATION_REQUESTS,
    );
    setThreads(hydratedThreads);
    if (groupMode === 'date') {
      setGroupedThreads(groupThreadsByDate(hydratedThreads));
    } else {
      setGroupedThreads(groupThreadsByInvestor(hydratedThreads));
    }
  }, [groupMode, groupThreadsByDate, groupThreadsByInvestor, user.id]);

  // Fetch threads when drawer opens
  useEffect(() => {
    if (open) {
      (async () => {
        setLoading(true);
        try {
          await refreshThreads();
        } catch (error) {
          console.error('Failed to fetch saved sessions:', error);
          antMessage.error('Failed to load saved sessions');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open, refreshThreads]);

  const handleThreadClick = async (thread: Thread) => {
    try {
      const restoredSession = await restorePlannerSession({
        threadId: thread.thread_id,
        dispatch,
        investors,
        fallbackThread: thread,
      });

      onSessionLoaded?.(
        thread.thread_id,
        restoredSession.plannerMode,
        restoredSession.plannerEngine,
      );

      // Close drawer after loading
      onClose();
      antMessage.success('Session loaded successfully');
    } catch (error) {
      console.error('Failed to load session:', error);
      antMessage.error('Failed to load session');
    }
  };

  const handleEditClick = (e: React.MouseEvent, thread: Thread) => {
    e.stopPropagation();

    setEditingThread(thread);
    setNewTitle(extractThreadNameFromState(thread, ''));
    setEditModalVisible(true);
  };

  const handleInvestorClick = (e: React.MouseEvent, thread: Thread) => {
    e.stopPropagation();
    setAssigningThread(thread);
    setSelectedInvestorId(thread.metadata?.investorId as string | undefined);
    setInvestorModalVisible(true);
  };

  const refreshThreadsWithLoading = useCallback(async () => {
    setLoading(true);
    try {
      await refreshThreads();
    } finally {
      setLoading(false);
    }
  }, [refreshThreads]);

  const handleDeleteClick = async (
    e: React.MouseEvent<HTMLElement> | undefined,
    thread: Thread,
  ) => {
    e?.stopPropagation();
    try {
      await deleteThread(thread.thread_id);
      antMessage.success('Session deleted successfully');
      await refreshThreadsWithLoading();
    } catch (error) {
      console.error('Failed to delete session:', error);
      antMessage.error('Failed to delete session');
    }
  };

  const handleSaveTitle = async () => {
    if (!editingThread || !newTitle.trim()) {
      return;
    }

    try {
      // Update thread name
      await patchThreadMetadata(editingThread.thread_id, {
        title: newTitle.trim(),
      });

      antMessage.success('Session title updated successfully');
      setEditModalVisible(false);
      await refreshThreadsWithLoading();
    } catch (error) {
      console.error('Failed to update session title:', error);
      antMessage.error('Failed to update session title');
    }
  };

  const handleSaveInvestor = async () => {
    if (!assigningThread) {
      return;
    }

    try {
      // Update metadata with investor ID
      const metadata: Record<string, unknown> = {};
      if (selectedInvestorId) {
        metadata.investorId = selectedInvestorId;
      } else {
        delete metadata.investorId;
      }
      await patchThreadMetadata(assigningThread.thread_id, metadata);

      antMessage.success('Client assigned successfully');
      setInvestorModalVisible(false);
      await refreshThreadsWithLoading();
    } catch (error) {
      console.error('Failed to assign client:', error);
      antMessage.error('Failed to assign client');
    }
  };

  return (
    <>
      <Drawer
        title="Saved Past Sessions"
        placement="left"
        onClose={onClose}
        open={open}
        width={400}
        className="session-history-drawer"
      >
        <div className="session-group-mode-selector">
          <Radio.Group
            value={groupMode}
            onChange={(e) => setGroupMode(e.target.value)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="date">By Date</Radio.Button>
          </Radio.Group>
        </div>
        {loading ? (
          <div className="session-loading">
            <Spin />
            <span>Loading saved sessions...</span>
          </div>
        ) : groupedThreads.length === 0 ? (
          <div className="empty-sessions">
            <Typography.Text type="secondary">No saved sessions found</Typography.Text>
          </div>
        ) : (
          <div className="session-history-content">
            {groupedThreads.map(({ key, label, threads }) => (
              <div key={key} className="session-group">
                <div className="session-group-header">
                  <Typography.Text strong className="group-title">
                    {label}
                  </Typography.Text>
                  <Typography.Text type="secondary" className="group-count">
                    ({threads.length} session{threads.length !== 1 ? 's' : ''})
                  </Typography.Text>
                </div>
                <List
                  className="session-group-list"
                  dataSource={threads}
                  renderItem={(thread) => {
                    const plannerMode = extractPlannerModeFromThreadState(thread);
                    const plannerEngine = extractPlannerEngineFromThreadState(thread);
                    return (
                      <List.Item
                        key={`${thread.thread_id}`}
                        className="session-history-item"
                        onClick={() => handleThreadClick(thread)}
                      >
                        <div className="session-item-content">
                          <div className="session-main-info">
                            <Typography.Text strong className="session-title">
                              {extractThreadNameFromState(thread)}
                            </Typography.Text>
                            <div className="session-meta">
                              <Typography.Text type="secondary" className="session-time">
                                {new Date(thread.updated_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </Typography.Text>
                              {plannerMode && (
                                <>
                                  <Typography.Text type="secondary" className="session-separator">
                                    •
                                  </Typography.Text>
                                  <Typography.Text
                                    type="secondary"
                                    className="session-planner-mode"
                                  >
                                    {PLANNER_MODE_LABELS[plannerMode]}
                                  </Typography.Text>
                                </>
                              )}
                              {plannerEngine && (
                                <>
                                  <Typography.Text type="secondary" className="session-separator">
                                    •
                                  </Typography.Text>
                                  <Typography.Text
                                    type="secondary"
                                    className="session-planner-engine"
                                  >
                                    {PLANNER_ENGINE_LABELS[plannerEngine]}
                                  </Typography.Text>
                                </>
                              )}
                              {(thread.metadata?.investorId as string | undefined) && (
                                <>
                                  <Typography.Text type="secondary" className="session-separator">
                                    •
                                  </Typography.Text>
                                  <Typography.Text type="secondary" className="session-investor">
                                    <UserOutlined style={{ marginRight: 4 }} />
                                    {investors.find(
                                      (candidate) => candidate.id === thread.metadata?.investorId,
                                    )?.name || 'Unknown'}
                                  </Typography.Text>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="session-actions">
                            <Tooltip title="Assign Client">
                              <UserOutlined
                                className="action-icon investor-icon"
                                onClick={(e) => handleInvestorClick(e, thread)}
                              />
                            </Tooltip>
                            <Tooltip title="Edit Title">
                              <EditOutlined
                                className="action-icon edit-icon"
                                onClick={(e) => handleEditClick(e, thread)}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Delete this session?"
                              description="Are you sure you want to delete this session? This action cannot be undone."
                              onConfirm={(e) => handleDeleteClick(e, thread)}
                              okText="Yes"
                              cancelText="No"
                              placement="left"
                            >
                              <DeleteOutlined
                                className="action-icon delete-icon"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <Modal
        title="Edit Session Title"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveTitle}>
            Save
          </Button>,
        ]}
      >
        <Input
          placeholder="Enter session title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onPressEnter={handleSaveTitle}
          autoFocus
        />
      </Modal>

      <Modal
        title="Assign Client"
        open={investorModalVisible}
        onCancel={() => setInvestorModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setInvestorModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveInvestor}>
            Assign
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            Assign this session to a client for better organization and tracking.
          </Typography.Text>
          <Select
            placeholder="Select a client"
            value={selectedInvestorId}
            onChange={(value) => setSelectedInvestorId(value)}
            loading={investorsLoadingState === 'pending'}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            options={sortInvestorsByDisplayName(investors).map((investor) => ({
              label: getInvestorDisplayName(investor),
              value: investor.id,
            }))}
          />
        </Space>
      </Modal>
    </>
  );
}

function ChatThinkingAnimation() {
  return (
    <div className="chat-thinking-animation">
      <div className="chat-thinking-animation-dot"></div>
      <div className="chat-thinking-animation-dot"></div>
      <div className="chat-thinking-animation-dot"></div>
    </div>
  );
}

function MeetingNotesModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [meetings, setMeetings] = useState<AttendedMeeting[]>([]);
  const [selectedMeetings, setSelectedMeetings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const additionalContext = useAppSelector(
    (state) => state.canvas.conversation.additionalContextJson,
  );

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const response = await getAttendedMeetings();
      setMeetings(response.meetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      message.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMeetingNotes = async () => {
    if (selectedMeetings.length === 0) {
      message.warning('Please select at least one meeting');
      return;
    }

    setSubmitting(true);
    try {
      // Fetch meeting notes content for selected meetings
      const meetingNotesContent = [];
      for (const meetingId of selectedMeetings) {
        const meeting = meetings.find((m) => m.job_id === meetingId);
        if (meeting?.summary_file_key) {
          try {
            const document = await getDocument(meeting.summary_file_key);
            meetingNotesContent.push({
              title: meeting.title,
              date: meeting.start_time,
              content: document.document.content,
            });
          } catch (error) {
            console.error(`Error fetching meeting notes for ${meeting.title}:`, error);
          }
        }
      }

      if (meetingNotesContent.length > 0) {
        // Add meeting notes to Wavvest Planning context
        const mergedContext = {
          ...(additionalContext || {}),
        } as AdditionalContextJson;
        dispatch(
          updateConversationAdditionalContextJson({
            ...mergedContext,
            meetingNotes: JSON.stringify(meetingNotesContent),
          }),
        );

        message.success(`Added ${meetingNotesContent.length} meeting notes to Wavvest Planning`);
        onClose();
        setSelectedMeetings([]);
      } else {
        message.error('No meeting notes content found for selected meetings');
      }
    } catch (error) {
      console.error('Error adding meeting notes:', error);
      message.error('Failed to add meeting notes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalCancel = () => {
    onClose();
    setSelectedMeetings([]);
  };

  useEffect(() => {
    if (isOpen) {
      fetchMeetings();
    }
  }, [isOpen]);

  return (
    <Modal
      title="Add Meeting Notes"
      open={isOpen}
      onOk={handleAddMeetingNotes}
      onCancel={handleModalCancel}
      confirmLoading={submitting}
      okText="Add to Wavvest Planning"
      cancelText="Cancel"
      width={600}
      okButtonProps={{
        disabled: selectedMeetings.length === 0 || meetings.length === 0,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <p>Select meetings to include their notes in your Wavvest Planning:</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : meetings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          </div>
          <h3 style={{ color: '#666', marginBottom: 8 }}>No Meeting Notes Available</h3>
          <p style={{ color: '#999', marginBottom: 16 }}>
            You haven't connected any meeting notes yet. To use meeting notes in your Wavvest
            Planning, please set up Meeting Hub first.
          </p>
          <Button type="primary" onClick={() => navigate('/home/meetinghub')}>
            Go to Meeting Hub Settings
          </Button>
        </div>
      ) : (
        <Select
          mode="multiple"
          placeholder="Select meetings to include"
          value={selectedMeetings}
          onChange={(values) => setSelectedMeetings(values)}
          style={{ width: '100%' }}
          size="large"
          showSearch
          filterOption={(input, option) =>
            (option?.label as React.ReactElement)?.props?.children[0]?.props?.children
              ?.toLowerCase()
              ?.includes(input.toLowerCase())
          }
          options={meetings.map((meeting) => ({
            label: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '4px 0',
                }}
              >
                <span style={{ fontWeight: 500 }}>{meeting.title}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {new Date(meeting.start_time).toLocaleDateString()} •
                  {meeting.summary_file_key ? ' Notes available' : ' No notes'}
                </span>
              </div>
            ),
            value: meeting.job_id,
            disabled: !meeting.summary_file_key, // Disable if no notes available
          }))}
        />
      )}

      {selectedMeetings.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: '#f6f6f6',
            borderRadius: 4,
          }}
        >
          <strong>Selected:</strong> {selectedMeetings.length} meeting
          {selectedMeetings.length !== 1 ? 's' : ''}
        </div>
      )}
    </Modal>
  );
}

// Data Context List Component - displays added data like file list
function DataContextList({ firmDataEnabled }: { firmDataEnabled: boolean }) {
  const dispatch = useAppDispatch();
  const { conversation } = useAppSelector((state) => state.canvas);

  const additionalContext = conversation.additionalContextJson || {};
  const hasPlaidData = additionalContext.plaidData;
  const hasMeetingNotes = additionalContext.meetingNotes;
  const hasApexData = additionalContext.apexData;
  const hasPlannerInvestorProfile = additionalContext.plannerInvestorProfile;
  const plannerInvestorProfileSubmitted =
    additionalContext.plannerInvestorProfileSubmitted === true;
  const plaidInvestorInfo = additionalContext.plaidInvestor;
  const apexInvestorInfo = additionalContext.apexInvestor;

  const handleRemoveData = (type: 'plaid' | 'meeting-notes' | 'apex' | 'investor-profile') => {
    if (type === 'plaid') {
      const newContext = { ...additionalContext };
      delete newContext.plaidData;
      delete newContext.plaidInvestor;
      dispatch(updateConversationAdditionalContextJson(newContext));
      antMessage.success('Plaid data removed');
    } else if (type === 'meeting-notes') {
      const newContext = { ...additionalContext };
      delete newContext.meetingNotes;
      dispatch(updateConversationAdditionalContextJson(newContext));
      antMessage.success('Meeting notes removed');
    } else if (type === 'apex') {
      const newContext = { ...additionalContext };
      delete newContext.apexData;
      delete newContext.apexInvestor;
      dispatch(updateConversationAdditionalContextJson(newContext));
      antMessage.success('Apex data removed');
    } else if (type === 'investor-profile') {
      const newContext = { ...additionalContext };
      delete newContext.plannerInvestorProfile;
      delete newContext.plannerInvestorProfileSubmitted;
      dispatch(updateConversationAdditionalContextJson(newContext));
      antMessage.success('Investor profile removed');
    }
  };

  // Get investor name for Plaid data
  const getInvestorName = () => {
    if (plaidInvestorInfo) {
      try {
        const investorInfo = JSON.parse(plaidInvestorInfo);
        return getInvestorNameOrFallback(investorInfo.name);
      } catch {
        return 'Unknown client';
      }
    }
    return 'Unknown client';
  };

  // Get investor name for Apex data
  const getApexInvestorName = () => {
    if (apexInvestorInfo) {
      try {
        const investorInfo = JSON.parse(apexInvestorInfo);
        return getInvestorNameOrFallback(investorInfo.name);
      } catch {
        return 'Unknown client';
      }
    }
    return 'Unknown client';
  };

  const getPlannerInvestorProfileName = () => {
    const selection = parsePlannerInvestorProfileSelection(hasPlannerInvestorProfile);
    return selection?.displayName ?? 'Selected investor';
  };

  // Get meeting notes count
  const getMeetingNotesCount = () => {
    if (hasMeetingNotes) {
      try {
        const meetingNotes = JSON.parse(hasMeetingNotes);
        return Array.isArray(meetingNotes) ? meetingNotes.length : 1;
      } catch {
        return 1;
      }
    }
    return 0;
  };

  const dataItems = [];

  if (hasPlaidData) {
    dataItems.push({
      key: 'plaid',
      type: 'plaid' as const,
      name: `${getInvestorName()}'s Plaid Data`,
      icon: <DatabaseOutlined />,
      canRemove: true,
    });
  }

  if (firmDataEnabled && hasPlannerInvestorProfile) {
    dataItems.push({
      key: 'investor-profile',
      type: 'investor-profile' as const,
      name: `${getPlannerInvestorProfileName()}'s Investor Profile`,
      icon: <UserOutlined />,
      canRemove: !plannerInvestorProfileSubmitted,
    });
  }

  if (hasMeetingNotes) {
    const count = getMeetingNotesCount();
    dataItems.push({
      key: 'meeting-notes',
      type: 'meeting-notes' as const,
      name: `${count} Meeting Note${count > 1 ? 's' : ''}`,
      icon: <FileTextOutlined />,
      canRemove: true,
    });
  }

  if (hasApexData) {
    dataItems.push({
      key: 'apex',
      type: 'apex' as const,
      name: `${getApexInvestorName()}'s Apex Data`,
      icon: <BankOutlined />,
      canRemove: true,
    });
  }

  if (!hasDataContextItems(additionalContext, firmDataEnabled)) {
    return null;
  }

  return (
    <div className="data-context-list">
      {dataItems.map((item) => (
        <div key={item.key} className="data-context-item" data-type={item.type}>
          <Tooltip title={item.name} placement="bottom">
            <div className="data-name">
              <span className="data-icon">{item.icon}</span>
              <span className="data-name-text">{item.name}</span>
            </div>
          </Tooltip>
          {item.canRemove ? (
            <button
              type="button"
              className="context-chip-remove-button"
              aria-label={`Remove ${item.name}`}
              onClick={() => handleRemoveData(item.type)}
            >
              <DeleteOutlined />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

type ApexDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function ApexDataModal({ isOpen, onClose }: ApexDataModalProps) {
  const dispatch = useAppDispatch();
  const correspondentId = useAppSelector(selectApexCorrespondentId);
  const investors = useAppSelector(selectInvestors);
  const investorsLoading = useAppSelector(selectInvestorsLoadingState) === 'pending';

  // Get pre-selected investor from Redux (if assigned in guided mode)
  const preSelectedInvestor = useAppSelector(
    (state) => state.planning.guidedMode.investorSelector.selectedInvestor,
  );

  // Initialize with pre-selected investor's cognito_id if available
  const getInitialInvestorId = useCallback(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return investor?.cognito_id || null;
    }
    return null;
  }, [preSelectedInvestor, investors]);

  const [selectedInvestor, setSelectedInvestor] = useState<string | null>(getInitialInvestorId);
  const [apexData, setApexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAutoFetched, setHasAutoFetched] = useState(false);
  // Use a ref to track which investor we've already fetched/attempted to fetch for
  const fetchedInvestorRef = useRef<string | null>(null);
  const additionalContext = useAppSelector(
    (state) => state.canvas.conversation.additionalContextJson,
  );

  const existingInvestors = useMemo(
    () => investors.filter((investor) => investor.cognito_id),
    [investors],
  );

  // Check if pre-selected investor is not onboarded (no cognito_id)
  const isPreSelectedNotOnboarded = useMemo(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return Boolean(investor && !investor.cognito_id);
    }
    return false;
  }, [preSelectedInvestor, investors]);

  const preSelectedInvestorName = useMemo(() => {
    if (preSelectedInvestor?.id) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      return investor
        ? getInvestorDisplayName(investor)
        : getInvestorNameOrFallback(preSelectedInvestor.name);
    }
    return '';
  }, [preSelectedInvestor, investors]);

  const fetchApexData = useCallback(async () => {
    if (!correspondentId || !selectedInvestor) {
      setError('Please select a client first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [accountData, accountOverview] = await Promise.all([
        getApexAccountData(correspondentId, selectedInvestor),
        getApexAccountOverview(selectedInvestor),
      ]);

      // Validate that we have meaningful data
      const hasAccountData = accountData?.accounts && accountData.accounts.length > 0;

      // Check if overview data has meaningful content (not just empty arrays)
      const hasOverviewData =
        accountOverview &&
        (() => {
          // If it's an empty object, no data
          if (Object.keys(accountOverview).length === 0) return false;

          // Check for common empty patterns
          if (
            accountOverview.accounts &&
            Array.isArray(accountOverview.accounts) &&
            accountOverview.accounts.length === 0
          ) {
            // If it only has empty accounts array and nothing else meaningful
            const otherKeys = Object.keys(accountOverview).filter(
              (key) => key !== 'accounts' && key !== 'errors',
            );
            const hasOtherMeaningfulData = otherKeys.some((key) => {
              const value = accountOverview[key];
              return (
                value !== null &&
                value !== undefined &&
                !(Array.isArray(value) && value.length === 0) &&
                !(typeof value === 'object' && Object.keys(value).length === 0)
              );
            });
            return hasOtherMeaningfulData;
          }

          return true;
        })();

      if (!hasAccountData && !hasOverviewData) {
        setError('No Apex account data found for this client');
        setApexData(null);
        return;
      }

      // Show warning if only partial data is available
      if (!hasAccountData) {
        antMessage.warning('Account details not available, but overview data loaded');
      }
      if (!hasOverviewData) {
        antMessage.warning('Overview data not available, but account details loaded');
      }

      const combinedData = {
        accountData,
        accountOverview,
      };

      setApexData(combinedData);
    } catch (err) {
      console.error('Error fetching Apex data:', err);
      setError('Failed to fetch Apex data');
    } finally {
      setLoading(false);
    }
  }, [correspondentId, selectedInvestor]);

  // Auto-populate and auto-fetch when modal opens with pre-selected investor
  // Reset state when modal closes to ensure proper re-initialization on next open
  useEffect(() => {
    if (isOpen && preSelectedInvestor?.id && !hasAutoFetched) {
      const investor = investors.find((candidate) => candidate.id === preSelectedInvestor.id);
      if (investor?.cognito_id) {
        setSelectedInvestor(investor.cognito_id);
        setHasAutoFetched(true);
      }
    }
    // Reset state when modal closes
    if (!isOpen) {
      setHasAutoFetched(false);
      setSelectedInvestor(null);
    }
  }, [isOpen, preSelectedInvestor, investors, hasAutoFetched]);

  // Auto-fetch when selectedInvestor changes and we have correspondentId
  // Use ref to prevent duplicate fetches for the same investor
  useEffect(() => {
    if (
      isOpen &&
      selectedInvestor &&
      correspondentId &&
      !loading &&
      fetchedInvestorRef.current !== selectedInvestor
    ) {
      fetchedInvestorRef.current = selectedInvestor;
      fetchApexData();
    }
  }, [isOpen, selectedInvestor, correspondentId, loading, fetchApexData]);

  const handleInvestorSelect = (investorId: string) => {
    setSelectedInvestor(investorId);
    setApexData(null);
    setError(null);
    fetchedInvestorRef.current = null;
  };

  const handleSubmit = () => {
    if (apexData && selectedInvestor) {
      const selectedInvestorData = investors.find(
        (investor) => investor.cognito_id === selectedInvestor,
      );
      const mergedContext = {
        ...(additionalContext || {}),
      } as AdditionalContextJson;
      dispatch(
        updateConversationAdditionalContextJson({
          ...mergedContext,
          apexData: JSON.stringify(apexData),
          apexInvestor: JSON.stringify({
            id: selectedInvestor,
            name: selectedInvestorData
              ? getInvestorDisplayName(selectedInvestorData)
              : 'Unknown client',
          }),
        }),
      );
      antMessage.success('Apex data added successfully');
      onClose();
    }
  };

  const handleModalCancel = () => {
    onClose();
    setSelectedInvestor(null);
    setApexData(null);
    setError(null);
    fetchedInvestorRef.current = null; // Reset the fetch tracker
  };

  // Note: Auto-fetch is handled by the useEffect above (lines 2624-2643)
  // which includes proper guards against re-fetching (!apexData && !loading)

  return (
    <Modal
      title="Select Client for Apex Data"
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleModalCancel}
      confirmLoading={false}
      okText="Add Apex Data"
      cancelText="Cancel"
      width={600}
      okButtonProps={{
        disabled: !apexData || loading || !!error || isPreSelectedNotOnboarded,
      }}
    >
      {isPreSelectedNotOnboarded ? (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '6px',
            marginBottom: '16px',
          }}
        >
          <div style={{ fontWeight: 500, marginBottom: '8px', color: '#d46b08' }}>
            Client Not Onboarded
          </div>
          <div style={{ color: '#874d00' }}>
            <strong>{preSelectedInvestorName}</strong> has not completed the onboarding process.
            Apex data is only available for clients who have linked their accounts.
          </div>
          <div style={{ marginTop: '12px', color: '#8c8c8c', fontSize: '13px' }}>
            Please select another client below, or ask this client to complete account linking
            first.
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <p>Select a client to load their Apex account data:</p>
        </div>
      )}

      {investorsLoading ? (
        <div className="apex-data-modal-loading">Loading clients...</div>
      ) : (
        <div className="apex-data-investor-select">
          <Select
            placeholder="Select a client"
            onChange={handleInvestorSelect}
            value={selectedInvestor}
            loading={investorsLoading}
            optionFilterProp="label"
            showSearch
            style={{ width: '100%', marginBottom: 16 }}
            dropdownStyle={{ minWidth: 400 }}
            listHeight={300}
          >
            {existingInvestors.length > 0
              ? existingInvestors.map((investor) => (
                  <Select.Option
                    key={investor.cognito_id}
                    value={investor.cognito_id}
                    label={getInvestorDisplayName(investor)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minWidth: 0,
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          flex: '1 1 auto',
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getInvestorDisplayName(investor)}
                        </div>
                      </div>
                      <div
                        style={{
                          flex: '0 0 auto',
                          fontSize: '12px',
                          color: '#666',
                          maxWidth: '40%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {investor.email}
                      </div>
                    </div>
                  </Select.Option>
                ))
              : null}
          </Select>
          {existingInvestors.length === 0 && (
            <div className="no-investors">No clients available</div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading Apex account data...</div>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <BankOutlined
              style={{
                fontSize: 48,
                color: error.includes('No Apex account data found') ? '#faad14' : '#ff4d4f',
              }}
            />
          </div>
          <h3
            style={{
              color: error.includes('No Apex account data found') ? '#faad14' : '#ff4d4f',
              marginBottom: 8,
            }}
          >
            {error.includes('No Apex account data found')
              ? 'No Apex Data Available'
              : 'Error Loading Apex Data'}
          </h3>
          <p style={{ color: '#999', marginBottom: 16 }}>
            {error.includes('No Apex account data found')
              ? "This client doesn't have any Apex account data available. Please ensure the client has active Apex accounts."
              : error}
          </p>
          <Button type="primary" onClick={fetchApexData}>
            {error.includes('No Apex account data found') ? 'Check Again' : 'Retry'}
          </Button>
        </div>
      ) : apexData ? (
        <div
          style={{
            padding: 16,
            backgroundColor: '#f6f6f6',
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <BankOutlined style={{ marginRight: 8, color: '#52c41a' }} />
            <strong style={{ color: '#52c41a' }}>Apex Data Ready</strong>
          </div>
          <p style={{ margin: 0, color: '#666' }}>
            {(() => {
              const hasAccounts = apexData.accountData?.accounts?.length > 0;

              // Use the same validation logic for overview data
              const hasOverview =
                apexData.accountOverview &&
                (() => {
                  const overview = apexData.accountOverview;
                  if (Object.keys(overview).length === 0) return false;

                  if (
                    overview.accounts &&
                    Array.isArray(overview.accounts) &&
                    overview.accounts.length === 0
                  ) {
                    const otherKeys = Object.keys(overview).filter(
                      (key) => key !== 'accounts' && key !== 'errors',
                    );
                    return otherKeys.some((key) => {
                      const value = overview[key];
                      return (
                        value !== null &&
                        value !== undefined &&
                        !(Array.isArray(value) && value.length === 0) &&
                        !(typeof value === 'object' && Object.keys(value).length === 0)
                      );
                    });
                  }

                  return true;
                })();

              const accountCount = hasAccounts ? apexData.accountData.accounts.length : 0;

              if (hasAccounts && hasOverview) {
                return `Loaded ${accountCount} Apex account${accountCount !== 1 ? 's' : ''} and overview data.`;
              } else if (hasAccounts) {
                return `Loaded ${accountCount} Apex account${accountCount !== 1 ? 's' : ''}.`;
              } else if (hasOverview) {
                return 'Loaded Apex overview data.';
              } else {
                return 'Apex account data has been loaded.';
              }
            })()}
          </p>
        </div>
      ) : null}
    </Modal>
  );
}

export default ConversationPanel;
export { ConversationPanelFooter };
